import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Modal, Badge, ProgressBar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import SignatureCanvas from 'react-signature-canvas';
import AddressAutocomplete from '../addressAutocomplete';
import ErrorBoundary from '../common/ErrorBoundary';
import { handleImageUpload } from '../../api/uploads';
import {
  submitChildRegistration,
  fetchRegistrationPackages,
  selectRegistrationById,
  selectSubmitLoading
} from '../../store/childRegistration/childRegistrationSlice';
import { submitAllergyActionPlan, fetchAllergyActionPlans, selectPlans as selectAllergyActionPlans } from '../../store/allergyActionPlan/allergyActionPlanSlice';
import { submitLunchPlanWaiver, fetchLunchPlanWaivers, selectWaivers as selectLunchPlanWaivers } from '../../store/lunchPlanWaiver/lunchPlanWaiverSlice';
import { submitNoPeanutPolicy, fetchNoPeanutPolicies, selectPolicies as selectNoPeanutPolicies } from '../../store/noPeanutPolicy/noPeanutPolicySlice';
import { getEffectiveStudentId, isParentLoginProfile, getSelectedChildProfile } from '../../utilities/teacherModulePermissions';

// The Registration Checklist Summary items are never toggled by hand - each
// one reflects real data collected elsewhere in the form (or a document
// uploaded for it). This is computed fresh from `data` every time it's
// needed (render, and again right before submit) rather than being cached
// as its own state field - a cached copy could go stale whenever `data` is
// bulk-replaced (e.g. a background reload of the saved record) without its
// specific source fields changing value, since nothing would then know to
// recompute it.
//
// `activeRecords` carries the "already on file" flags for the 3 sections
// embedded further down (Allergy Action Plan / Lunch Plan Waiver / No
// Peanut Policy) - those live on the profile, not on `data`, so they can't
// be derived from `data` alone the way everything else here is.
// Not needed right now - flip back to true to bring the "Payment" category
// (Pre-Authorized Debit Form) back into the Registration Checklist Summary.
// It's always been an auto-checked, non-blocking item (see
// checklist_debit_form below), so this only hides the category from the
// summary; nothing else depends on it being shown.
const SHOW_PAYMENT_CHECKLIST_CATEGORY = false;

const computeDerivedChecklist = (data, activeRecords = {}) => {
  const clausesSigned = Object.keys(data)
    .filter((key) => key.startsWith('contract_initial_') && data[key]).length;

  // Meal Waiver is satisfied by actually completing the Lunch Plan Waiver
  // section above (either a record already on file, or filled in this
  // session) - no separate document needed.
  const mealWaiverCovered = !!(
    activeRecords.hasLunchWaiver ||
    (data.lunch_waiver_opt_in && data.lunch_waiver_reason && data.lunch_waiver_signature && data.lunch_waiver_signature_date)
  );
  // Shown as "No Peanut Policy" (backend field is still checklist_waivers)
  // - satisfied by completing the No Peanut Policy section above.
  const noPeanutPolicyCovered = !!(
    activeRecords.hasNoPeanutPolicy ||
    (data.peanut_policy_acknowledge && data.peanut_policy_signature && data.peanut_policy_signature_date)
  );

  return {
    checklist_registration_form: !!(data.child_first_name && data.child_last_name && data.child_dob),
    checklist_emergency_form: !!(data.emergency_contact_name && (data.emergency_contact_phone || data.emergency_contact_cell)),
    checklist_child_info_page: !!(data.child_first_name && data.child_dob && data.child_address),
    checklist_signed_contract: clausesSigned === 13,
    checklist_parent_handbook: !!data.contract_initial_1,
    checklist_offsite_form: !!(data.offsite_parent_name && data.offsite_signature),
    checklist_birth_certificate: !!data.birth_certificate_document,
    checklist_health_forms: !!data.health_immunization_document,
    checklist_medical_reports: !!data.medical_reports_document,
    checklist_waivers: noPeanutPolicyCovered,
    checklist_meal_waiver: mealWaiverCovered,
    // Pre-Authorized Debit Form and "All About Me" Form aren't backed by a
    // document — always checked
    checklist_debit_form: true,
    checklist_all_about_me: true
  };
};

// Loads the 13 contract clauses' signed state + signature image from a
// saved registration record. The backend's real field is contract_clauseN_initial
// (no underscore before the number - confirmed against RegistrationDetailModal.js,
// which already renders these same clauses correctly), holding either a
// signature (an https:// URL or an inline data:image/... URI) or a plain
// 1/true flag for an initial with no image. contract_clause_N_initial (with
// underscore) is also checked in case a record was ever saved under that
// name. A prior version of this check only matched values containing
// "http", which silently missed data:image signatures and is why a signed
// clause could still render with no image.
const extractContractClauseData = (reg) => {
  const result = {};
  const isSignatureValue = (value) =>
    typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:image'));

  for (let n = 1; n <= 13; n++) {
    const withUnderscore = reg?.[`contract_clause_${n}_initial`];
    const noUnderscore = reg?.[`contract_clause${n}_initial`];
    const signatureUrl =
      isSignatureValue(withUnderscore) ? withUnderscore :
      isSignatureValue(noUnderscore) ? noUnderscore :
      null;

    result[`contract_signature_${n}`] = signatureUrl || reg?.[`contract_signature_${n}`] || '';
    result[`contract_initial_${n}`] = !!(
      signatureUrl ||
      withUnderscore == 1 || withUnderscore === true ||
      noUnderscore == 1 || noUnderscore === true ||
      reg?.[`contract_initial_${n}`] == 1 || reg?.[`contract_initial_${n}`] === true
    );
  }
  return result;
};

// Uploads a signature pad's drawing to S3 and returns the URL to use for
// its field - the newly-uploaded one if a fresh signature was drawn, or
// the pad's existing value unchanged if there's nothing new to upload.
//
// "Nothing new" is decided one of two ways:
// - dirtyRef given (an onEnd-tracked ref - see its declaration for why):
//   trust it. The pad only counts as having something new if the parent
//   actually drew a stroke on it, regardless of whether it also happens to
//   look non-empty (which it will, for as long as an existing signature
//   was successfully pre-loaded onto it).
// - dirtyRef omitted: fall back to the original heuristic (pad non-empty
//   AND currentValue doesn't already look like a saved http URL). This
//   assumes an already-loaded signature and a hand-drawn replacement are
//   never both visible on the pad at once without the field's value having
//   been cleared first - true as long as pre-loading it never silently
//   fails, which is why the 3 fields prone to that (see
//   loadSignatureIntoPad) pass a dirtyRef instead of relying on this.
//
// Never throws: if the pad's own toDataURL() throws - notably a
// SecurityError from a tainted canvas, confirmed to happen in practice and
// previously left uncaught here, which failed the *entire* registration
// submit over a single signature pad that didn't even need re-uploading -
// or the upload itself fails, this logs it and falls back to the existing
// value instead. One signature that doesn't get saved is a much smaller
// problem than a submission that hard-fails.
const uploadSignatureIfDrawn = async (padRef, currentValue, filenamePrefix, dirtyRef) => {
  const pad = padRef.current;
  if (!pad || pad.isEmpty()) return currentValue;
  const hasSomethingNew = dirtyRef ? dirtyRef.current : !(currentValue && currentValue.includes('http'));
  if (!hasSomethingNew) return currentValue;

  try {
    const dataURL = pad.toDataURL('image/png');
    const blob = await fetch(dataURL).then((res) => res.blob());
    const file = new File([blob], `${filenamePrefix}_${Date.now()}.png`, { type: 'image/png' });
    return await handleImageUpload(file, 'signatures/', 'image/png');
  } catch (e) {
    console.error(`Could not upload ${filenamePrefix} signature - keeping the existing value:`, e);
    return currentValue;
  }
};

const ChildRegistrationFormCompInner = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [searchParams] = useSearchParams();

  // Get registration data from navigation state if available
  const navigationRegistrationData = location?.state?.registrationData;
  // Which child (registration_packages.user_id) the `:id` in the URL
  // actually belongs to - set by ChildRegistrationListComp when it links
  // here. `id` is the registration_packages row id, not a student id, so it
  // says nothing on its own about which child to fetch; this is the only
  // record-specific signal that survives a hard refresh (navigation `state`
  // above does not - the browser drops it on reload).
  const studentIdFromUrl = searchParams.get('studentId');

  // Signature refs
  const arrivalSignaturePadRef = useRef(null);
  const emergencyMedicalSignaturePadRef = useRef(null);
  const mediaReleaseSignaturePadRef = useRef(null);
  const checklistSignaturePadRef = useRef(null);
  const contractParentSignaturePadRef = useRef(null);
  const contractFinancialSignaturePadRef = useRef(null);
  const offsiteSignaturePadRef = useRef(null);
  const contractClauseSignatureRef = useRef(null);
  // Signature pads for the 3 sections embedded below - only ever mounted
  // (and only ever read from) while that section's fields are showing, i.e.
  // while there's no active record for it yet - see activeAllergyPlans /
  // activeLunchWaivers / activeNoPeanutPolicies further down.
  const allergyPlanSignaturePadRef = useRef(null);
  const lunchWaiverSignaturePadRef = useRef(null);
  const peanutPolicySignaturePadRef = useRef(null);
  // Tracks whether the parent has actually drawn on each of these 3 pads
  // (set via each SignatureCanvas's onEnd, which only fires for a real
  // pointer/touch stroke - never from the programmatic fromDataURL() load
  // in loadSignatureIntoPad below). This is the signal handleSubmit uses to
  // decide whether to upload a new signature for these 3 fields - not
  // "does the pad already look non-empty", because when the existing
  // signature's reload silently fails (e.g. a CORS hiccup) the pad looks
  // blank while formData still holds the old saved URL, and the parent
  // reasonably draws straight into what looks like an empty pad without
  // clicking "Clear Signature" first. Without this flag that draw would be
  // silently discarded: the old URL would still look present and "correct"
  // from a currentValue-only check, and the empty-looking pad would end up
  // sent back unchanged instead of what was actually just drawn.
  const allergyPlanSignatureDirtyRef = useRef(false);
  const lunchWaiverSignatureDirtyRef = useRef(false);
  const peanutPolicySignatureDirtyRef = useRef(false);

  // Contract signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentClauseIndex, setCurrentClauseIndex] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);

  // Tracks which checklist document (by formData field name) is currently uploading
  const [checklistDocUploading, setChecklistDocUploading] = useState(null);
  // Inline validation errors for checklist document uploads, keyed by field name
  const [checklistDocErrors, setChecklistDocErrors] = useState({});

  // Authorized pickup persons state - track active person slots
  const [activePickupPersons, setActivePickupPersons] = useState([1]);

  // Autosave (draft) state - see performAutosave/handleFormBlur further down
  // for the actual save logic. 'idle' | 'saving' | 'saved' | 'error'.
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [autoSaveError, setAutoSaveError] = useState(null);
  // Short settle-debounce so tabbing through several fields quickly fires
  // one save after the user actually stops, not one per field blurred.
  const blurSaveTimerRef = useRef(null);
  // Snapshot (JSON string) of formData as of the last successful save (or
  // the initial load) - a blur where nothing actually changed since then
  // is a no-op, not another round-trip to the server.
  const lastSavedFormDataRef = useRef(null);
  // In edit mode, an existing record's data arrives asynchronously - true
  // once it's actually landed in formData, so autosave never fires on the
  // still-blank default form and overwrites/misjudges the real record. Add
  // mode has nothing to wait for, so it starts ready.
  const autoSaveReadyRef = useRef(!isEditMode);
  // Set once this session's first draft save creates a brand-new record (add
  // mode only - edit mode already has its own id from the URL), so every
  // save after that updates the same draft instead of relying solely on the
  // backend's "reuse this child's existing package" fallback.
  const [draftId, setDraftId] = useState(null);

  const existingRegistration = useSelector((state) => selectRegistrationById(state, id));
  const submitLoading = useSelector(selectSubmitLoading);

  // Get user profile data to auto-populate child name
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;

  // Get general settings for the parent handbook URL
  let generalSettingsData = useSelector((state) => state.setting.setting.general_settings);
  generalSettingsData = generalSettingsData ? JSON.parse(generalSettingsData) : null;
  // A parent viewing a child's registration packet should prefill the child's
  // name, not the parent's own — the parent's own record has no child fields.
  const namePrefillSource = isParentLoginProfile(profileData) ? getSelectedChildProfile(profileData) : profileData;

  // Allergy Action Plan / Lunch Plan Waiver / No Peanut Policy each already
  // have their own dedicated record and their own step in the "Registration
  // Packet" tab (user-profile.js), read there off `displayProfile` - the
  // same isParentLoginProfile/getSelectedChildProfile resolution already
  // computed above as namePrefillSource, so a parent with multiple children
  // gets the currently selected child's records here too, not their own
  // account's. All 3 sections below pre-fill their fields from the existing
  // record (when there is one) and leave them editable in place, rather
  // than collecting the same data twice in two different places - see
  // existingAllergyPlan / existingLunchWaiver / existingNoPeanutPolicy below.
  //
  // Deliberately NOT using registrationPacketStatus's filterActiveRecords
  // (isStandardStepRecordFilled) here - that requires record.status === 1,
  // which is how the separately-fetched *list* records are shaped, but
  // every other place in the app that reads this exact embedded field
  // (friend-profile.js's document cards, user-profile.js's own read-only
  // "Documents" tab a few hundred lines below) just checks the object is
  // present, with no status field involved. Requiring status === 1 here
  // silently hid a real, already-filled record any time it came back
  // without a numeric 1 status (missing entirely, or a "1" string) - which
  // is exactly why these fields were showing up as not-yet-filled.
  const firstEmbeddedRecord = (value) => {
    if (Array.isArray(value)) return value[0] || null;
    if (value && typeof value === 'object' && Object.keys(value).length > 0) return value;
    return null;
  };
  const existingAllergyPlanRecord = firstEmbeddedRecord(namePrefillSource?.allergy_action_plan);
  const existingLunchWaiverRecord = firstEmbeddedRecord(namePrefillSource?.lunch_plan_waiver);
  const existingNoPeanutPolicyRecord = firstEmbeddedRecord(namePrefillSource?.no_peanut_policy);

  // The embedded record above is a lighter summary the profile payload
  // carries alongside everything else - fine for the text fields, but its
  // signature URL was consistently missing/stale, which is why it never
  // drew onto the pad no matter how the loading was wired. The dedicated
  // list endpoints (the same ones AllergyActionPlanListComp,
  // LunchPlanWaiverListComp and NoPeanutPolicyListComp already fetch, and
  // the same ones the standalone edit pages read via selectWaiverById /
  // selectPolicyById to show their own "Existing Parent Signature" preview
  // successfully) return the full record. Fetching them here too and
  // layering that over the embedded summary - once it arrives - gives the
  // signature fields a source that's actually proven to carry a working URL.
  const fetchedAllergyPlans = useSelector(selectAllergyActionPlans);
  const fetchedLunchWaivers = useSelector(selectLunchPlanWaivers);
  const fetchedNoPeanutPolicies = useSelector(selectNoPeanutPolicies);

  useEffect(() => {
    const studentId = getEffectiveStudentId(profileData);
    if (!studentId) return;
    dispatch(fetchAllergyActionPlans({ page: 1, limit: 20, student_id: studentId }));
    dispatch(fetchLunchPlanWaivers({ page: 1, limit: 20, student_id: studentId }));
    dispatch(fetchNoPeanutPolicies({ page: 1, limit: 20, student_id: studentId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picks the most recently created active record (highest id) rather than
  // just the first one the API happens to return. A student should only
  // ever have one active record per section, but earlier bugs in this same
  // form (now fixed) could send an update without the record's id and have
  // the backend create a new one instead of updating the existing one,
  // leaving more than one active record behind - in which case showing/
  // editing whichever one is oldest, or whichever the API lists first, is
  // exactly how a change that really did save could still look like it
  // didn't: the edit lands on the new record while the old one (unchanged)
  // is what keeps being displayed.
  const pickMostRecentActive = (list) => {
    const active = (Array.isArray(list) ? list : []).filter((record) => record?.status === 1);
    if (active.length === 0) return null;
    return active.reduce((latest, record) => (Number(record.id) > Number(latest.id) ? record : latest));
  };

  const fetchedActiveAllergyPlan = pickMostRecentActive(fetchedAllergyPlans);
  const fetchedActiveLunchWaiver = pickMostRecentActive(fetchedLunchWaivers);
  const fetchedActiveNoPeanutPolicy = pickMostRecentActive(fetchedNoPeanutPolicies);

  // Merge: embedded summary first (available immediately, no network
  // round-trip needed), the authoritative fetched record layered on top
  // once it's in - so an id, or a signature, present on one but not the
  // other still comes through either way.
  const activeAllergyPlans = existingAllergyPlanRecord || fetchedActiveAllergyPlan
    ? [{ ...existingAllergyPlanRecord, ...fetchedActiveAllergyPlan }]
    : [];
  const activeLunchWaivers = existingLunchWaiverRecord || fetchedActiveLunchWaiver
    ? [{ ...existingLunchWaiverRecord, ...fetchedActiveLunchWaiver }]
    : [];
  const activeNoPeanutPolicies = existingNoPeanutPolicyRecord || fetchedActiveNoPeanutPolicy
    ? [{ ...existingNoPeanutPolicyRecord, ...fetchedActiveNoPeanutPolicy }]
    : [];
  // Passed into computeDerivedChecklist so its "No Peanut Policy" / "Meal
  // Waiver" items can see these sections' on-file status - see that
  // function's comment for why they can't be derived from formData alone.
  const checklistActiveRecords = {
    hasLunchWaiver: activeLunchWaivers.length > 0,
    hasNoPeanutPolicy: activeNoPeanutPolicies.length > 0
  };

  const [formData, setFormData] = useState({
    // ID for edit mode
    id: null,

    // Child Information
    child_first_name: namePrefillSource?.first_name || '',
    child_last_name: namePrefillSource?.last_name || '',
    child_dob: '',
    child_age: '',
    child_address: '',
    child_city: '',
    child_postal_code: '',

    // Parent/Guardian 1 Information
    parent1_first_name: '',
    parent1_last_name: '',
    parent1_phone: '',
    parent1_cell: '',
    parent1_email: '',
    parent1_address: '',
    parent1_city: '',
    parent1_state: '',
    parent1_zip: '',
    parent1_work_phone: '',
    parent1_work_address: '',
    parent1_work_city: '',
    parent1_work_postal_code: '',
    parent1_relation: 'Mother',

    // Parent/Guardian 2 Information
    parent2_first_name: '',
    parent2_last_name: '',
    parent2_phone: '',
    parent2_cell: '',
    parent2_email: '',
    parent2_address: '',
    parent2_city: '',
    parent2_state: '',
    parent2_zip: '',
    parent2_work_phone: '',
    parent2_work_address: '',
    parent2_work_city: '',
    parent2_work_postal_code: '',
    parent2_relation: 'Father',
    same_address: false,
    same_as_mother_address: false,

    // Emergency Contact Information
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_address: '',
    emergency_contact_city: '',
    emergency_contact_postal_code: '',
    emergency_contact_phone: '',
    emergency_contact_cell: '',
    emergency_contact_work_phone: '',
    emergency_contact_work_address: '',
    emergency_contact_work_city: '',
    emergency_contact_work_postal_code: '',
    emergency_contact_email: '',

    // Medical Information
    pediatrician_name: '',
    pediatrician_phone: '',
    medical_conditions: '',
    allergies: '',
    medications: '',

    // Additional Information
    special_needs: '',
    previous_daycare: '',
    enrollment_date: '',
    notes: '',

    // Authorized Pick-Up List (4 entries)
    pickup1_name: '',
    pickup1_phone: '',
    pickup1_cell: '',
    pickup1_address: '',
    pickup1_city: '',
    pickup1_postal_code: '',
    pickup2_name: '',
    pickup2_phone: '',
    pickup2_cell: '',
    pickup2_address: '',
    pickup2_city: '',
    pickup2_postal_code: '',
    pickup3_name: '',
    pickup3_phone: '',
    pickup3_cell: '',
    pickup3_address: '',
    pickup3_city: '',
    pickup3_postal_code: '',
    pickup4_name: '',
    pickup4_phone: '',
    pickup4_cell: '',
    pickup4_address: '',
    pickup4_city: '',
    pickup4_postal_code: '',

    // Arrival and Departure
    dropoff_time: '',
    pickup_time: '',
    parent_guardian_signature: '',
    executive_director_signature: '',
    start_date: '',
    withdrawal_date: '',

    // Emergency Medical Authorization
    emergency_auth_parent_name: '',
    emergency_auth_child_name: namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : '',
    family_physician_name: '',
    physician_address: '',
    physician_city: '',
    physician_postal_code: '',
    physician_phone: '',
    current_medications: '',
    health_allergies: '',
    sleep_time: '',
    wake_time: '',
    favourite_activity: '',
    emergency_auth_signature: '',
    emergency_auth_date: '',

    // Child Information Page
    agency_involvement: '',
    vision_history: '',
    hearing_concerns: '',
    diagnoses: '',
    communicable_disease: '',
    eating_concerns: '',
    developmental_concerns: '',
    pets_at_home: '',
    siblings_info: '',
    favourite_toy: '',

    // Media Release Waiver
    media_release_agree: false,
    media_release_child_name: namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : '',
    media_release_date: '',
    media_release_parent_name: '',
    media_release_signature: '',

    // Off-Site Permission
    offsite_agree: false,
    offsite_parent_name: '',
    offsite_child_name: namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : '',
    offsite_signature: '',
    offsite_date: '',

    // Contract Terms - 13 clauses with signatures
    contract_initial_1: false,
    contract_initial_2: false,
    contract_initial_3: false,
    contract_initial_4: false,
    contract_initial_5: false,
    contract_initial_6: false,
    contract_initial_7: false,
    contract_initial_8: false,
    contract_initial_9: false,
    contract_initial_10: false,
    contract_initial_11: false,
    contract_initial_12: false,
    contract_initial_13: false,
    contract_signature_1: '',
    contract_signature_2: '',
    contract_signature_3: '',
    contract_signature_4: '',
    contract_signature_5: '',
    contract_signature_6: '',
    contract_signature_7: '',
    contract_signature_8: '',
    contract_signature_9: '',
    contract_signature_10: '',
    contract_signature_11: '',
    contract_signature_12: '',
    contract_signature_13: '',

    // Parent/Guardian Information (for signing)
    parent_print_name: '',
    parent_signature: '',
    parent_signature_date: '',

    // Financially Responsible Party
    financial_print_name: '',
    financial_signature: '',
    financial_signature_date: '',
    financial_agency: '',

    // Registration Checklist - the checklist_* completion flags themselves
    // are NOT stored here. They're always computed fresh from the fields
    // below (and elsewhere in this state) by computeDerivedChecklist, so
    // there's never a cached copy that can go stale against the real data.
    parent_checklist_signature: '',
    parent_checklist_date: '',
    staff_witness_signature: '',
    checklist_staff_date: '',

    // Uploaded documents backing the checklist items above (jpg/png/pdf) -
    // each checklist_* flag for these is derived from the matching document
    // being present, not toggled by hand.
    birth_certificate_document: '',
    health_immunization_document: '',
    medical_reports_document: '',
    waivers_document: '',
    meal_waiver_document: '',
    // Original file names, shown next to the uploaded document links above
    birth_certificate_document_name: '',
    health_immunization_document_name: '',
    medical_reports_document_name: '',
    waivers_document_name: '',
    meal_waiver_document_name: '',

    // Allergy Action Plan - optional, opt-in while no record exists yet.
    // Once one does, allergy_plan_id gets set (see the pre-fill effect
    // below) and these same fields are shown pre-filled and editable
    // instead, so editing them here updates that record rather than
    // creating a duplicate.
    allergy_plan_id: null,
    allergy_plan_opt_in: false,
    allergy_plan_is_peanuts: 0,
    allergy_plan_is_shellfish: 0,
    allergy_plan_shellfish_specify: '',
    allergy_plan_is_tree_nuts: 0,
    allergy_plan_is_fish: 0,
    allergy_plan_fish_specify: '',
    allergy_plan_is_milk: 0,
    allergy_plan_is_food_additives: 0,
    allergy_plan_food_additives_specify: '',
    allergy_plan_all_dairy_products: 0,
    allergy_plan_insect_stings_bites: 0,
    allergy_plan_insect_stings_bites_specify: '',
    allergy_plan_insect_stings_bites_medication: '',
    allergy_plan_is_other: 0,
    allergy_plan_other_specify: '',
    allergy_plan_symptoms: '',
    allergy_plan_emergency_treatment_procedures: '',
    allergy_plan_require_epi_pen: 0,
    allergy_plan_location_of_epi_pen: '',
    allergy_plan_is_allergy_form: 1,
    allergy_plan_is_preference_form: 0,
    allergy_plan_signature: '',
    allergy_plan_signature_date: '',

    // Lunch Plan Waiver - optional, opt-in while no record exists yet, then
    // pre-filled and editable once lunch_waiver_id is set (same pattern as
    // the Allergy Action Plan section above).
    lunch_waiver_id: null,
    lunch_waiver_opt_in: false,
    lunch_waiver_reason: '',
    lunch_waiver_signature: '',
    lunch_waiver_signature_date: '',

    // No Peanut Policy - a required acknowledgement (like the Contract
    // Terms clauses above). Always shown, pre-filled and editable once
    // peanut_policy_id is set.
    peanut_policy_id: null,
    peanut_policy_acknowledge: false,
    peanut_policy_signature: '',
    peanut_policy_signature_date: ''
  });

  // Fetches an existing signature URL as a blob and draws it directly onto
  // a signature pad - same approach the big "Load signatures into pads"
  // effect further down uses for every other signature in this form (a
  // bare <img src=URL> would permanently taint the canvas on a
  // cross-origin S3 URL - see that effect's comment). Called from each of
  // the 3 sections' own pre-fill effects below, right where their record
  // becomes available, rather than only via that other effect - which
  // reaches these 3 pads through a formData round-trip (record arrives ->
  // this pre-fills formData -> that *other* effect notices formData
  // changed -> only then loads the pad), an extra hop that, in a form with
  // this many effects/state updates competing around mount, left the
  // pad's own 100ms debounce timer prone to being reset before it ever
  // got to run. Returns a cleanup function, in the exact shape a
  // useEffect callback expects.
  const loadSignatureIntoPad = (padRef, url, label) => {
    if (!url) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId;

    const tryDraw = () => {
      attempts += 1;
      const pad = padRef.current;
      if (!pad) {
        if (attempts < 10) timeoutId = setTimeout(tryDraw, 150);
        return;
      }
      if (!pad.isEmpty()) return;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`Signature fetch failed: ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (cancelled || !padRef.current || !padRef.current.isEmpty()) return;

          // react-signature-canvas only sizes the canvas's actual pixel
          // drawing buffer once, in its own componentDidMount (from
          // offsetWidth/offsetHeight at that instant) - clearOnResize=false
          // (set on these pads) is what stops it resyncing that on a later
          // resize event too (needed so a stray window resize can't wipe a
          // signature the user just drew). In a form this size, with 3 pads
          // and this many effects settling around mount, the buffer baked
          // in at mount is often stale/undersized by the time this fetch
          // resolves - drawImage() then silently draws into a too-small
          // buffer and nothing shows up, even though isEmpty() correctly
          // flips to false. Re-measure and resync the buffer to the pad's
          // actual current on-screen size right before drawing.
          const canvasEl = padRef.current.getCanvas();
          if (canvasEl.offsetWidth > 0 && canvasEl.offsetHeight > 0) {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvasEl.width = canvasEl.offsetWidth * ratio;
            canvasEl.height = canvasEl.offsetHeight * ratio;
            canvasEl.getContext('2d').scale(ratio, ratio);
          }

          const objectUrl = URL.createObjectURL(blob);
          padRef.current.fromDataURL(objectUrl, {
            width: canvasEl.offsetWidth || undefined,
            height: canvasEl.offsetHeight || undefined
          });
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        })
        .catch((e) => {
          // Deliberately no fallback to a direct (non-blob) draw here: that
          // was tried and confirmed harmful - it taints the canvas, and on
          // a later submit (no new signature drawn, so this section's own
          // "already has an http URL, skip re-upload" check was expected to
          // protect it) toDataURL() still threw a SecurityError anyway and
          // broke the entire registration submit, not just this pad's
          // reload. A signature that doesn't preview here is a real but
          // much smaller problem than a submission that hard-fails - see
          // the try/catch around this pad's toDataURL() call in
          // handleSubmit too, which is the actual backstop against a
          // tainted canvas (however it happens) breaking submission.
          console.log(`Could not load ${label} signature via blob fetch - leaving the pad blank:`, e);
        });
    };

    timeoutId = setTimeout(tryDraw, 100);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  };

  // The Allergy Action Plan section shows its fields pre-filled (and
  // editable) the moment a record already exists, rather than the
  // read-only card the other 2 sections use - see the comment above
  // activeAllergyPlans. This is the one active record (if any) driving
  // that; [0] is safe because a child only ever has one active plan.
  const existingAllergyPlan = activeAllergyPlans[0] || null;

  // Pre-fill from that record as soon as it's available, keyed on its id
  // (not the array/object itself, which is a fresh reference from
  // filterActiveRecords/profileData on every render) so this doesn't
  // re-run - and stomp on an in-progress edit - on every keystroke
  // elsewhere in the form.
  useEffect(() => {
    if (!existingAllergyPlan) return;
    setFormData(prev => ({
      ...prev,
      allergy_plan_id: existingAllergyPlan.id ?? null,
      allergy_plan_is_peanuts: existingAllergyPlan.is_peanuts ?? 0,
      allergy_plan_is_shellfish: existingAllergyPlan.is_shellfish ?? 0,
      allergy_plan_shellfish_specify: existingAllergyPlan.shellfish_specify || '',
      allergy_plan_is_tree_nuts: existingAllergyPlan.is_tree_nuts ?? 0,
      allergy_plan_is_fish: existingAllergyPlan.is_fish ?? 0,
      allergy_plan_fish_specify: existingAllergyPlan.fish_specify || '',
      allergy_plan_is_milk: existingAllergyPlan.is_milk ?? 0,
      allergy_plan_is_food_additives: existingAllergyPlan.is_food_additives ?? 0,
      allergy_plan_food_additives_specify: existingAllergyPlan.food_additives_specify || '',
      allergy_plan_all_dairy_products: existingAllergyPlan.all_dairy_products ?? 0,
      allergy_plan_insect_stings_bites: existingAllergyPlan.insect_stings_bites ?? 0,
      allergy_plan_insect_stings_bites_specify: existingAllergyPlan.insect_stings_bites_specify || '',
      allergy_plan_insect_stings_bites_medication: existingAllergyPlan.insect_stings_bites_medication || '',
      allergy_plan_is_other: existingAllergyPlan.is_other ?? 0,
      allergy_plan_other_specify: existingAllergyPlan.other_specify || '',
      allergy_plan_symptoms: existingAllergyPlan.symptoms || '',
      allergy_plan_emergency_treatment_procedures: existingAllergyPlan.emergency_treatment_procedures || '',
      allergy_plan_require_epi_pen: existingAllergyPlan.require_epi_pen ?? 0,
      allergy_plan_location_of_epi_pen: existingAllergyPlan.location_of_epi_pen || '',
      allergy_plan_is_allergy_form: existingAllergyPlan.is_allergy_form ?? 1,
      allergy_plan_is_preference_form: existingAllergyPlan.is_preference_form ?? 0,
      allergy_plan_signature: existingAllergyPlan.parent_signature || '',
      allergy_plan_signature_date: formatDateForAPI(existingAllergyPlan.parent_signature_date)
    }));
    return loadSignatureIntoPad(allergyPlanSignaturePadRef, existingAllergyPlan.parent_signature, 'Allergy Action Plan');
    // Depends on both halves of the merge (embedded summary + fetched
    // authoritative record) separately, not just the merged existingAllergyPlan.id
    // - if both happen to carry the same id, that id alone wouldn't change
    // when the fetched half arrives and updates the merged signature, so this
    // effect would never notice/re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAllergyPlanRecord?.id, fetchedActiveAllergyPlan?.id]);

  // Whether the Allergy Action Plan fields should render at all - either
  // there's already a record to edit, or the parent has opted in to filling
  // out a new one. Used for both the JSX below and handleSubmit's
  // validation/upload/dispatch gating for this section.
  const allergyPlanFieldsVisible = !!existingAllergyPlan || formData.allergy_plan_opt_in;

  // Lunch Plan Waiver - same "pre-fill and leave editable" treatment as
  // Allergy Action Plan above.
  const existingLunchWaiver = activeLunchWaivers[0] || null;

  useEffect(() => {
    if (!existingLunchWaiver) return;
    setFormData(prev => ({
      ...prev,
      lunch_waiver_id: existingLunchWaiver.id ?? null,
      lunch_waiver_reason: existingLunchWaiver.reason || '',
      lunch_waiver_signature: existingLunchWaiver.parents_signature || '',
      lunch_waiver_signature_date: formatDateForAPI(existingLunchWaiver.parent_signature_date)
    }));
    return loadSignatureIntoPad(lunchWaiverSignaturePadRef, existingLunchWaiver.parents_signature, 'Lunch Plan Waiver');
    // Same reasoning as the Allergy Action Plan effect above for depending
    // on both merge halves separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLunchWaiverRecord?.id, fetchedActiveLunchWaiver?.id]);

  const lunchWaiverFieldsVisible = !!existingLunchWaiver || formData.lunch_waiver_opt_in;

  // No Peanut Policy - always required, so unlike the two sections above
  // there's no opt-in gate: its fields are always visible, pre-filled from
  // the existing record when there is one and editable either way.
  const existingNoPeanutPolicy = activeNoPeanutPolicies[0] || null;

  useEffect(() => {
    if (!existingNoPeanutPolicy) return;
    setFormData(prev => ({
      ...prev,
      peanut_policy_id: existingNoPeanutPolicy.id ?? null,
      // An existing record only ever gets created by submitting this
      // acknowledgement in the first place, so its presence already implies
      // "acknowledged" - there's no separate acknowledge flag on the
      // backend record itself.
      peanut_policy_acknowledge: true,
      peanut_policy_signature: existingNoPeanutPolicy.parent_signature || '',
      peanut_policy_signature_date: formatDateForAPI(existingNoPeanutPolicy.parent_signature_date)
    }));
    return loadSignatureIntoPad(peanutPolicySignaturePadRef, existingNoPeanutPolicy.parent_signature, 'No Peanut Policy');
    // Same reasoning as the Allergy Action Plan effect above for depending
    // on both merge halves separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingNoPeanutPolicyRecord?.id, fetchedActiveNoPeanutPolicy?.id]);

  // Fetch registration data when in edit mode (only if not provided via navigation)
  //
  // On a hard page refresh, Redux state is empty and profileData (parsed
  // from state.setting.setting.user_data) can take a render or two to
  // rehydrate. effectiveStudentId wasn't a dependency here before, so if
  // this effect's first run happened before profileData was ready, it
  // fired the fetch with an undefined/wrong student_id and then NEVER
  // retried once profileData did arrive - the classic symptom being "I
  // just saved and the response had the new data, but refreshing the page
  // shows the old data again" (it's not stale data being *shown* so much
  // as the right record never actually being *fetched*). Depending on the
  // resolved id (a primitive) instead of the whole profileData object also
  // avoids re-fetching on every unrelated profileData reference change.
  //
  // studentIdFromUrl (this specific record's own child) takes priority over
  // effectiveStudentId (whichever child happens to be "currently selected"
  // app-wide) - for a parent with more than one child, those are not the
  // same thing. `:id` here is a registration_packages row id, so a parent
  // editing child B's packet while child A is the active selection would,
  // fetching by effectiveStudentId alone, pull child A's package - which
  // never contains a record whose id matches the URL, leaving
  // existingRegistration permanently undefined and the form stuck showing
  // stale/blank data after every refresh. Falling back to effectiveStudentId
  // keeps this working for links that predate the ?studentId= param below.
  const effectiveStudentId = getEffectiveStudentId(profileData);
  const fetchStudentId = studentIdFromUrl || effectiveStudentId;
  useEffect(() => {
    if (isEditMode && id && !navigationRegistrationData && fetchStudentId) {
      dispatch(fetchRegistrationPackages({ page: 1, limit: 100, student_id: fetchStudentId }));
    }
  }, [dispatch, isEditMode, id, navigationRegistrationData, fetchStudentId]);

  useEffect(() => {
    // Use navigation data if available, otherwise use store data
    const registrationToLoad = navigationRegistrationData || existingRegistration;
    
    if (isEditMode && registrationToLoad) {
      // Map API field names to form field names
      const reg = registrationToLoad;
      
      // Debug: Log the child address fields from backend response
      console.log('Loading Registration Data - Child Address Fields:', {
        user_setting_address: reg.user_details?.user_setting?.address,
        child_address: reg.child_address,
        child_home_address: reg.child_home_address,
        child_city: reg.child_city,
        child_postal_code: reg.child_postal_code,
        all_address_fields: Object.keys(reg).filter(key => key.toLowerCase().includes('address'))
      });

      const resolvedChildDob = formatDateForAPI(
        reg.child_date_of_birth ||
        reg.child_dob ||
        reg.date_of_birth ||
        reg.user_details?.user_setting?.dob ||
        ''
      );
      
      // Merges onto the previous state (functional updater) rather than
      // replacing it outright - a plain `setFormData({...})` here would
      // wipe out every field not explicitly listed below on every fire,
      // including the Allergy Action Plan / Lunch Plan Waiver / No Peanut
      // Policy fields the 3 sections' own pre-fill effects (above) just
      // set: those effects run once per record load and don't re-fire just
      // because this effect fires again afterward, so a wholesale replace
      // here would permanently blank those 3 sections out the moment an
      // existing Child Registration record loads - i.e. every "update" and
      // "view" of a registration, while "fill up" (no existing record, this
      // effect never firing) looked fine.
      setFormData(prev => ({
        ...prev,
        id: reg.id || id, // Include ID for edit mode
        // Loaded so the autosave effect below can tell whether this record
        // was already fully submitted (1) before this edit session started,
        // vs. still a draft (0) or brand new - see its own comment.
        status: reg.status,
        child_first_name: reg.child_first_name || profileData?.first_name || '',
        child_last_name: reg.child_last_name || profileData?.last_name || '',
        child_dob: resolvedChildDob,
        child_age: resolvedChildDob ? calculateAge(resolvedChildDob).toString() : (reg.child_age || ''),
        child_address: reg.user_details?.user_setting?.address || reg.child_address || reg.child_home_address || '',
        child_city: reg.child_city || '',
        child_postal_code: reg.child_postal_code || '',
        // Mother fields -> parent1
        parent1_first_name: reg.mother_name ? (reg.mother_name.trim().split(/\s+/).filter(p => p)[0] || '') : (reg.parent1_first_name || ''),
        parent1_last_name: reg.mother_name ? (reg.mother_name.trim().split(/\s+/).filter(p => p).slice(1).join(' ') || '') : (reg.parent1_last_name || ''),
        parent1_phone: reg.mother_home_phone || reg.parent1_phone || '',
        parent1_cell: reg.mother_cell || reg.parent1_cell || '',
        parent1_email: reg.mother_email || reg.parent1_email || '',
        parent1_address: reg.mother_home_address || reg.parent1_address || '',
        parent1_city: reg.mother_city || reg.parent1_city || '',
        parent1_state: reg.parent1_state || '',
        parent1_zip: reg.mother_postal_code || reg.parent1_zip || '',
        parent1_work_phone: reg.mother_work_phone || reg.parent1_work_phone || '',
        parent1_work_address: reg.mother_work_address || reg.parent1_work_address || '',
        parent1_work_city: reg.mother_work_city || reg.parent1_work_city || '',
        parent1_work_postal_code: reg.mother_work_postal_code || reg.parent1_work_postal_code || '',
        parent1_relation: reg.parent1_relation || 'Mother',
        // Father fields -> parent2
        parent2_first_name: reg.father_name ? (reg.father_name.trim().split(/\s+/).filter(p => p)[0] || '') : (reg.parent2_first_name || ''),
        parent2_last_name: reg.father_name ? (reg.father_name.trim().split(/\s+/).filter(p => p).slice(1).join(' ') || '') : (reg.parent2_last_name || ''),
        parent2_phone: reg.father_home_phone || reg.parent2_phone || '',
        parent2_cell: reg.father_cell || reg.parent2_cell || '',
        parent2_email: reg.father_email || reg.parent2_email || '',
        parent2_address: reg.father_home_address || reg.parent2_address || '',
        parent2_city: reg.father_city || reg.parent2_city || '',
        parent2_state: reg.parent2_state || '',
        parent2_zip: reg.father_postal_code || reg.parent2_zip || '',
        parent2_work_phone: reg.father_work_phone || reg.parent2_work_phone || '',
        parent2_work_address: reg.father_work_address || reg.parent2_work_address || '',
        parent2_work_city: reg.father_work_city || reg.parent2_work_city || '',
        parent2_work_postal_code: reg.father_work_postal_code || reg.parent2_work_postal_code || '',
        parent2_relation: reg.parent2_relation || 'Father',
        same_address: reg.same_address || false,
        same_as_mother_address: reg.same_as_mother_address || false,
        // Emergency Contact with relationship mapping
        emergency_contact_name: reg.emergency_contact_name || '',
        emergency_contact_relation: reg.emergency_contact_relationship || reg.emergency_contact_relation || '',
        emergency_contact_address: reg.emergency_contact_home_address || reg.emergency_contact_address || '',
        emergency_contact_city: reg.emergency_contact_city || '',
        emergency_contact_postal_code: reg.emergency_contact_postal_code || '',
        emergency_contact_phone: reg.emergency_contact_phone || '',
        emergency_contact_cell: reg.emergency_contact_cell || '',
        emergency_contact_work_phone: reg.emergency_contact_work_phone || '',
        emergency_contact_work_address: reg.emergency_contact_work_address || '',
        emergency_contact_email: reg.emergency_contact_email || '',
        pediatrician_name: reg.pediatrician_name || '',
        pediatrician_phone: reg.pediatrician_phone || '',
        medical_conditions: reg.medical_conditions || '',
        allergies: reg.allergies_health_info || reg.allergies || '',
        medications: reg.current_medications || reg.medications || '',
        special_needs: reg.special_needs || '',
        previous_daycare: reg.previous_daycare || '',
        enrollment_date: reg.enrollment_date || '',
        notes: reg.notes || '',
        // Pickup persons
        pickup1_name: reg.pickup_person1_name || reg.pickup1_name || '',
        pickup1_phone: reg.pickup_person1_phone || reg.pickup1_phone || '',
        pickup1_cell: reg.pickup_person1_cell || reg.pickup1_cell || '',
        pickup1_address: reg.pickup_person1_address || reg.pickup1_address || '',
        pickup1_city: reg.pickup_person1_city || reg.pickup1_city || '',
        pickup1_postal_code: reg.pickup_person1_postal_code || reg.pickup1_postal_code || '',
        pickup2_name: reg.pickup_person2_name || reg.pickup2_name || '',
        pickup2_phone: reg.pickup_person2_phone || reg.pickup2_phone || '',
        pickup2_cell: reg.pickup_person2_cell || reg.pickup2_cell || '',
        pickup2_address: reg.pickup_person2_address || reg.pickup2_address || '',
        pickup2_city: reg.pickup_person2_city || reg.pickup2_city || '',
        pickup2_postal_code: reg.pickup_person2_postal_code || reg.pickup2_postal_code || '',
        pickup3_name: reg.pickup_person3_name || reg.pickup3_name || '',
        pickup3_phone: reg.pickup_person3_phone || reg.pickup3_phone || '',
        pickup3_cell: reg.pickup_person3_cell || reg.pickup3_cell || '',
        pickup3_address: reg.pickup_person3_address || reg.pickup3_address || '',
        pickup3_city: reg.pickup_person3_city || reg.pickup3_city || '',
        pickup3_postal_code: reg.pickup_person3_postal_code || reg.pickup3_postal_code || '',
        pickup4_name: reg.pickup_person4_name || reg.pickup4_name || '',
        pickup4_phone: reg.pickup_person4_phone || reg.pickup4_phone || '',
        pickup4_cell: reg.pickup_person4_cell || reg.pickup4_cell || '',
        pickup4_address: reg.pickup_person4_address || reg.pickup4_address || '',
        pickup4_city: reg.pickup_person4_city || reg.pickup4_city || '',
        pickup4_postal_code: reg.pickup_person4_postal_code || reg.pickup4_postal_code || '',
        dropoff_time: reg.drop_off_time || reg.dropoff_time || '',
        pickup_time: reg.pick_up_time || reg.pickup_time || '',
        parent_guardian_signature: reg.parent_guardian_signature || '',
        executive_director_signature: reg.executive_director_signature || '',
        start_date: reg.start_date || '',
        withdrawal_date: reg.withdrawal_date || '',
        emergency_auth_parent_name: reg.emergency_auth_parent_guardian || reg.emergency_auth_parent_name || '',
        emergency_auth_child_name: reg.emergency_auth_child_name || (namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : ''),
        family_physician_name: reg.family_physician_name || '',
        physician_address: reg.physician_address || '',
        physician_phone: reg.physician_phone_number || reg.physician_phone || '',
        current_medications: reg.current_medications || '',
        health_allergies: reg.allergies_health_info || reg.health_allergies || '',
        sleep_time: reg.sleep_time_night || reg.sleep_time || '',
        wake_time: reg.wake_time_morning || reg.wake_time || '',
        favourite_activity: reg.child_favourite_activity || reg.favourite_activity || '',
        emergency_auth_signature: reg.emergency_auth_signature || '',
        emergency_auth_date: reg.emergency_auth_date || '',
        agency_involvement: reg.child_related_agencies_involvement || reg.agency_involvement || '',
        vision_history: reg.vision_history_glasses || reg.vision_history || '',
        hearing_concerns: reg.hearing_concerns_audiologist || reg.hearing_concerns || '',
        diagnoses: reg.diagnoses_adhd_delays || reg.diagnoses || '',
        communicable_disease: reg.communicable_disease_history || reg.communicable_disease || '',
        eating_concerns: reg.eating_appetite_concerns || reg.eating_concerns || '',
        developmental_concerns: reg.developmental_concerns || '',
        pets_at_home: reg.pets_at_home || '',
        siblings_info: reg.siblings_family_info || reg.siblings_info || '',
        favourite_toy: reg.child_favourite_toy_activity || reg.favourite_toy || '',
        media_release_agree: reg.media_release_agree === 1 || reg.media_release_agree === '1' || reg.media_release_agree === true ? true : (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false ? false : null),
        // buildRegistrationApiPayload below writes the child name / date /
        // parent name / signature to the "_disagree"-suffixed fields when
        // media_release_agree is false (see its own media_release_agree
        // branch) - reading only the "_agree" variants here regardless of
        // which way the record was saved silently dropped a previously
        // entered "I DISAGREE" section's data every time it was reopened for
        // editing, and re-submitting would then overwrite the real saved
        // values with blanks.
        media_release_child_name: (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false
          ? reg.media_release_child_name_disagree
          : reg.media_release_child_name_agree) || reg.media_release_child_name || (namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : ''),
        media_release_date: (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false
          ? reg.media_release_date_disagree
          : reg.media_release_date_agree) || reg.media_release_date || '',
        media_release_parent_name: (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false
          ? reg.media_release_parent_name_disagree
          : reg.media_release_parent_name_agree) || reg.media_release_parent_name || '',
        media_release_signature: (reg.media_release_agree === 0 || reg.media_release_agree === '0' || reg.media_release_agree === false
          ? reg.media_release_signature_disagree
          : reg.media_release_signature_agree) || reg.media_release_signature || '',
        offsite_agree: reg.offsite_permission === 1 || reg.offsite_permission === '1' || reg.offsite_permission === true || reg.offsite_agree === 1 || reg.offsite_agree === '1' || reg.offsite_agree === true ? true : (reg.offsite_permission === 0 || reg.offsite_permission === '0' || reg.offsite_permission === false || reg.offsite_agree === 0 || reg.offsite_agree === '0' || reg.offsite_agree === false ? false : null),
        offsite_parent_name: reg.offsite_permission_parent_name || reg.offsite_parent_name || '',
        offsite_child_name: reg.offsite_permission_child_name || reg.offsite_child_name || (namePrefillSource ? `${namePrefillSource.first_name || ''} ${namePrefillSource.last_name || ''}`.trim() : ''),
        offsite_signature: reg.offsite_permission_signature || reg.offsite_signature || '',
        offsite_date: reg.offsite_permission_date || reg.offsite_date || '',
        // Contract clauses (signed state + signature image) - see
        // extractContractClauseData for why both field-name variants are checked.
        ...extractContractClauseData(reg),
        parent_print_name: reg.contract_parent_print_name || reg.parent_print_name || '',
        parent_signature: reg.contract_parent_signature || reg.parent_signature || '',
        parent_signature_date: reg.contract_parent_date || reg.parent_signature_date || '',
        financial_print_name: reg.financial_party_print_name || reg.financial_print_name || '',
        financial_signature: reg.financial_party_signature || reg.financial_signature || '',
        financial_signature_date: reg.financial_party_date || reg.financial_signature_date || '',
        financial_agency: reg.financial_party_agency || reg.financial_agency || '',
        // Note: no checklist_* completion flags are loaded here - they're
        // always computed fresh by computeDerivedChecklist from the fields
        // loaded above/below, never trusted from the saved record.
        parent_checklist_signature: reg.checklist_parent_signature || reg.parent_checklist_signature || '',
        parent_checklist_date: reg.checklist_parent_date || reg.parent_checklist_date || '',
        staff_witness_signature: reg.checklist_staff_signature || reg.staff_witness_signature || '',
        checklist_staff_date: reg.checklist_staff_date || '',
        // Checklist-backing documents. The backend field names for these 3
        // are health_form_document / medical_report_document /
        // birth_certificate_document (see apiPayload below) - the
        // health_immunization_document / medical_reports_document fallbacks
        // are only for a record saved under the older field name.
        birth_certificate_document: reg.birth_certificate_document || '',
        health_immunization_document: reg.health_form_document || reg.health_immunization_document || '',
        medical_reports_document: reg.medical_report_document || reg.medical_reports_document || '',
        waivers_document: reg.waivers_document || '',
        meal_waiver_document: reg.meal_waiver_document || '',
        birth_certificate_document_name: reg.birth_certificate_document_name || '',
        health_immunization_document_name: reg.health_form_document_name || reg.health_immunization_document_name || '',
        medical_reports_document_name: reg.medical_report_document_name || reg.medical_reports_document_name || '',
        waivers_document_name: reg.waivers_document_name || '',
        meal_waiver_document_name: reg.meal_waiver_document_name || ''
      }));

      // Debug logging for contract signatures
      console.log('Loading registration data - Contract Signatures:', {
        contract_signature_1: (typeof reg.contract_clause1_initial === 'string' && reg.contract_clause1_initial.includes('http')) ? reg.contract_clause1_initial : (reg.contract_signature_1 || ''),
        contract_signature_2: (typeof reg.contract_clause2_initial === 'string' && reg.contract_clause2_initial.includes('http')) ? reg.contract_clause2_initial : (reg.contract_signature_2 || ''),
        contract_signature_3: (typeof reg.contract_clause3_initial === 'string' && reg.contract_clause3_initial.includes('http')) ? reg.contract_clause3_initial : (reg.contract_signature_3 || ''),
      });

      console.log('Loading registration data - Addresses:', {
        child_address: reg.child_address,
        mother_home_address: reg.mother_home_address,
        parent1_address: reg.parent1_address,
        father_home_address: reg.father_home_address,
        parent2_address: reg.parent2_address
      });

      // Set active pickup persons based on which ones have data
      const activePersons = [1];
      if (reg.pickup_person2_name || reg.pickup2_name) activePersons.push(2);
      if (reg.pickup_person3_name || reg.pickup3_name) activePersons.push(3);
      if (reg.pickup_person4_name || reg.pickup4_name) activePersons.push(4);
      setActivePickupPersons(activePersons);

      // Only now is it safe for the autosave effect below to act on
      // formData changes - before this, formData is still the blank
      // default, and an autosave firing on that would save empty data over
      // (or misjudge the draft/submitted status of) this real record.
      autoSaveReadyRef.current = true;
    }
  }, [isEditMode, existingRegistration, navigationRegistrationData]);

  // Seeds the blur-autosave dirty-check baseline (see performAutosave
  // above) to whatever formData ends up as once loading actually finishes -
  // add mode is ready immediately (autoSaveReadyRef starts true), so this
  // seeds to the blank default on mount; edit mode only seeds once the
  // effect above both loads the real record AND flips autoSaveReadyRef,
  // since this only acts the first time both are true. Without this, the
  // very first blur after an existing record loads would look "changed"
  // relative to a null baseline and fire a pointless save of identical data.
  useEffect(() => {
    if (autoSaveReadyRef.current && lastSavedFormDataRef.current === null) {
      lastSavedFormDataRef.current = JSON.stringify(formData);
    }
  }, [formData]);

  // Load signatures into signature pads when form data ID is set (edit
  // mode) - or, for the 3 sections below (Allergy Action Plan / Lunch Plan
  // Waiver / No Peanut Policy), whenever their own existing record has a
  // signature to load, regardless of whether the Child Registration record
  // itself is being edited. Same mechanism for all of them, so every
  // signature pad in this form behaves consistently: a saved signature
  // appears already drawn on the pad (clearable/replaceable), not as a
  // separate static preview next to an empty pad.
  useEffect(() => {
    if ((!isEditMode || !formData.id) && !existingAllergyPlan && !existingLunchWaiver && !existingNoPeanutPolicy) return;

    // Media release / offsite signature pads are conditionally rendered
    // (based on media_release_agree / offsite_agree), so their refs may not
    // be attached yet on the first tick. Retry briefly instead of giving up
    // after a single fixed delay - that's what made loading feel random.
    const signaturesToLoad = [
      { ref: arrivalSignaturePadRef, value: formData.parent_guardian_signature },
      { ref: emergencyMedicalSignaturePadRef, value: formData.emergency_auth_signature },
      { ref: mediaReleaseSignaturePadRef, value: formData.media_release_signature },
      { ref: offsiteSignaturePadRef, value: formData.offsite_signature },
      { ref: checklistSignaturePadRef, value: formData.parent_checklist_signature },
      { ref: contractParentSignaturePadRef, value: formData.parent_signature },
      { ref: contractFinancialSignaturePadRef, value: formData.financial_signature },
      { ref: allergyPlanSignaturePadRef, value: formData.allergy_plan_signature },
      { ref: lunchWaiverSignaturePadRef, value: formData.lunch_waiver_signature },
      { ref: peanutPolicySignaturePadRef, value: formData.peanut_policy_signature }
    ];

    let attempts = 0;
    let timeoutId;
    let cancelled = false;
    // Tracks which pads we've already kicked a load off for, so a retry tick
    // (waiting on some other pad that isn't attached yet) doesn't re-fetch
    // one that's already in flight or already loaded.
    const startedFor = new Set();

    const loadSignatures = () => {
      attempts += 1;
      let allAttached = true;

      signaturesToLoad.forEach(({ ref, value }) => {
        if (!value) return;
        if (!ref.current) {
          allAttached = false;
          return;
        }
        if (startedFor.has(ref) || !ref.current.isEmpty()) return;
        startedFor.add(ref);

        // Saved signature values are S3 URLs - a different origin than this
        // app. Drawing a cross-origin <img> straight onto the canvas (what
        // SignatureCanvas.fromDataURL does with a plain URL) permanently
        // taints it: every later toDataURL() call - i.e. re-saving this pad,
        // even after the user clears and re-signs - then throws a
        // SecurityError instead of producing an image. Fetch the image as a
        // blob first and hand the pad a same-origin blob: URL instead, which
        // draws without tainting the canvas; if the bucket has no CORS policy
        // the fetch simply fails and we leave the pad blank rather than
        // tainting it.
        fetch(value)
          .then((res) => {
            if (!res.ok) throw new Error(`Signature fetch failed: ${res.status}`);
            return res.blob();
          })
          .then((blob) => {
            if (cancelled || !ref.current || !ref.current.isEmpty()) return;

            // react-signature-canvas only sizes the canvas's actual pixel
            // drawing buffer once, in its own componentDidMount (from
            // offsetWidth/offsetHeight at that instant) - clearOnResize=false
            // (set on these pads) is what stops it resyncing that on a
            // later resize event too (needed so a stray window resize can't
            // wipe a signature the user just drew). With up to 10 pads and
            // this many effects settling around mount, the buffer baked in
            // at mount is often stale/undersized by the time this fetch
            // resolves - drawImage() then silently draws into a too-small
            // buffer and nothing shows up, even though isEmpty() correctly
            // flips to false. Re-measure and resync the buffer to the pad's
            // actual current on-screen size right before drawing.
            const canvasEl = ref.current.getCanvas();
            if (canvasEl.offsetWidth > 0 && canvasEl.offsetHeight > 0) {
              const ratio = Math.max(window.devicePixelRatio || 1, 1);
              canvasEl.width = canvasEl.offsetWidth * ratio;
              canvasEl.height = canvasEl.offsetHeight * ratio;
              canvasEl.getContext('2d').scale(ratio, ratio);
            }

            const objectUrl = URL.createObjectURL(blob);
            ref.current.fromDataURL(objectUrl, {
              width: canvasEl.offsetWidth || undefined,
              height: canvasEl.offsetHeight || undefined
            });
            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
          })
          .catch((e) => {
            console.log('Could not load signature:', e);
          });
      });

      if (!allAttached && attempts < 10) {
        timeoutId = setTimeout(loadSignatures, 150);
      }
    };

    timeoutId = setTimeout(loadSignatures, 100);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // Depends on existingAllergyPlan/existingLunchWaiver/existingNoPeanutPolicy
    // by id only (below), not the record objects themselves - see the
    // comment on those dependencies for why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    formData.id,
    formData.parent_guardian_signature,
    formData.emergency_auth_signature,
    formData.media_release_signature,
    formData.offsite_signature,
    formData.parent_checklist_signature,
    formData.parent_signature,
    formData.financial_signature,
    formData.media_release_agree,
    formData.offsite_agree,
    formData.allergy_plan_signature,
    formData.lunch_waiver_signature,
    formData.peanut_policy_signature,
    // Keyed on id, not the record objects themselves - those are freshly
    // reconstructed (JSON.parse'd) from Redux on every render regardless of
    // whether the underlying data changed, so depending on the objects
    // directly would re-run this effect on every render instead of only
    // when a plan/waiver/policy is actually (un)loaded.
    existingAllergyPlan?.id,
    existingLunchWaiver?.id,
    existingNoPeanutPolicy?.id
  ]);


  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Helper function to format date as YYYY-MM-DD string only (no time/timezone)
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return '';
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // Otherwise, parse and format
    try {
      const date = new Date(dateValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  // Previously treated the first typed digit as a country code and grouped
  // the rest 3-3-3 (e.g. a normal 10-digit "5551234567" became
  // "+5-551-234-567" instead of a recognizable number), for every phone
  // field in this form (any field whose name includes "phone" - see
  // handleInputChange below). Matches the corrected version already used in
  // EmergencyContactFormComp.js, and the ≥10-digit (no country code)
  // assumption this file's own validation already makes on parent1_phone.
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '').slice(0, 10);
    if (phoneNumber.length <= 3) return phoneNumber;
    if (phoneNumber.length <= 6) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (field, value) => {
    if (field === 'child_first_name' || field === 'child_last_name') {
      setFormData(prev => {
        const updated = { ...prev, [field]: value };
        const fullChildName = `${updated.child_first_name || ''} ${updated.child_last_name || ''}`.trim();
        return {
          ...updated,
          emergency_auth_child_name: fullChildName,
          media_release_child_name: fullChildName,
          offsite_child_name: fullChildName
        };
      });
    } else if (
      field === 'emergency_auth_child_name' ||
      field === 'media_release_child_name' ||
      field === 'offsite_child_name'
    ) {
      // These 3 sections show the child's name independently — keep them all
      // in sync no matter which one the user edits directly.
      setFormData(prev => ({
        ...prev,
        emergency_auth_child_name: value,
        media_release_child_name: value,
        offsite_child_name: value
      }));
    } else if (field === 'child_dob') {
      // Ensure date is stored as YYYY-MM-DD string only
      const dateOnly = formatDateForAPI(value);
      const age = calculateAge(dateOnly);
      setFormData(prev => ({
        ...prev,
        [field]: dateOnly,
        child_age: age.toString()
      }));
    } else if (field.includes('_date') || field.includes('date_')) {
      // Format all date fields to YYYY-MM-DD
      const dateOnly = formatDateForAPI(value);
      setFormData(prev => ({
        ...prev,
        [field]: dateOnly
      }));
    } else if (field.includes('phone')) {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [field]: formattedPhone
      }));
    } else if (field === 'same_address') {
      setFormData(prev => {
        if (value) {
          return {
            ...prev,
            [field]: value,
            parent2_address: prev.parent1_address,
            parent2_city: prev.parent1_city,
            parent2_state: prev.parent1_state,
            parent2_zip: prev.parent1_zip
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    } else if (field === 'same_as_mother_address') {
      setFormData(prev => {
        if (value) {
          const motherHomeAddressInput = document.getElementById('parent1_address');
          const motherHomeAddress = motherHomeAddressInput?.value?.trim() || prev.parent1_address;

          // Only mirror the home address fields — work fields stay
          // independent and editable regardless of this checkbox.
          return {
            ...prev,
            [field]: value,
            parent2_address: motherHomeAddress,
            parent2_city: prev.parent1_city,
            parent2_state: prev.parent1_state,
            parent2_zip: prev.parent1_zip
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    } else if (field.startsWith('parent1_') && formData.same_as_mother_address) {
      // If mother's home address fields are updated and checkbox is checked, update father's too.
      // Work fields are intentionally excluded — they stay independent.
      const addressFields = ['address', 'city', 'state', 'zip'];
      const fieldSuffix = field.replace('parent1_', '');
      
      if (addressFields.includes(fieldSuffix)) {
        setFormData(prev => ({
          ...prev,
          [field]: value,
          [`parent2_${fieldSuffix}`]: value
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Generic address handler that supports all address types
  const handleAddressSelect = (addressType, addressData, addressFieldMapping = {}) => {
    // Parse Google Places address components
    const parseAddressComponents = (details) => {
      const components = details?.address_components || [];
      let city = '';
      let state = '';
      let zip = '';

      components.forEach(component => {
        const types = component.types;
        if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (types.includes('postal_code')) {
          zip = component.long_name;
        }
      });

      return { city, state, zip };
    };

    // Default field mapping if not provided
    const defaultMappings = {
      'parent1': { address: 'parent1_address', city: 'parent1_city', state: 'parent1_state', zip: 'parent1_zip' },
      'parent2': { address: 'parent2_address', city: 'parent2_city', state: 'parent2_state', zip: 'parent2_zip' },
      'parent1_work': { address: 'parent1_work_address', city: 'parent1_work_city', postal: 'parent1_work_postal_code' },
      'parent2_work': { address: 'parent2_work_address', city: 'parent2_work_city', postal: 'parent2_work_postal_code' },
      'child': { address: 'child_address', city: 'child_city', postal: 'child_postal_code' },
      'emergency_contact': { address: 'emergency_contact_address', city: 'emergency_contact_city', postal: 'emergency_contact_postal_code' },
      'emergency_contact_work': { address: 'emergency_contact_work_address', city: 'emergency_contact_work_city', postal: 'emergency_contact_work_postal_code' },
      'physician': { address: 'physician_address', city: 'physician_city', postal: 'physician_postal_code' },
      'pickup1': { address: 'pickup1_address', city: 'pickup1_city', postal: 'pickup1_postal_code' },
      'pickup2': { address: 'pickup2_address', city: 'pickup2_city', postal: 'pickup2_postal_code' },
      'pickup3': { address: 'pickup3_address', city: 'pickup3_city', postal: 'pickup3_postal_code' },
      'pickup4': { address: 'pickup4_address', city: 'pickup4_city', postal: 'pickup4_postal_code' }
    };

    const mapping = addressFieldMapping || defaultMappings[addressType] || {};
    const parsed = parseAddressComponents(addressData.details);
    const updateData = { [mapping.address]: addressData.address || '' };

    // Add city if field exists
    if (mapping.city) {
      updateData[mapping.city] = parsed.city || '';
    }

    // Add state if field exists (for parent addresses)
    if (mapping.state) {
      updateData[mapping.state] = parsed.state || '';
    }

    // Add zip/postal if field exists
    if (mapping.zip) {
      updateData[mapping.zip] = parsed.zip || '';
    } else if (mapping.postal) {
      updateData[mapping.postal] = parsed.zip || '';
    }

    // Special handling for parent2 home address disabled state
    if (addressType === 'parent2' && (formData.same_address || formData.same_as_mother_address)) {
      return; // Don't update if address is disabled
    }

    setFormData(prev => {
      const mirroredFields = {};

      // Keep father address in sync when "Same as Mother's Address" is enabled.
      if (prev.same_as_mother_address && addressType === 'parent1') {
        mirroredFields.parent2_address = updateData.parent1_address || '';
        mirroredFields.parent2_city = updateData.parent1_city || '';
        mirroredFields.parent2_state = updateData.parent1_state || '';
        mirroredFields.parent2_zip = updateData.parent1_zip || '';
      }

      // Work address fields intentionally do not mirror — they stay independent.

      return {
        ...prev,
        ...updateData,
        ...mirroredFields
      };
    });
  };

  const CHECKLIST_DOC_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  const CHECKLIST_DOC_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf'];
  const CHECKLIST_DOC_MAX_SIZE_MB = 10;

  // Upload a checklist-backing document (jpg/png/pdf) and store its URL.
  // The matching checklist_* checkbox auto-checks itself via the derivation
  // effect once the field it reads from is populated.
  const handleChecklistDocumentUpload = async (fieldName, file) => {
    if (!file) return;

    setChecklistDocErrors((prev) => ({ ...prev, [fieldName]: null }));

    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isAllowedType =
      CHECKLIST_DOC_ALLOWED_TYPES.includes(file.type) || CHECKLIST_DOC_ALLOWED_EXTENSIONS.includes(extension);
    if (!isAllowedType) {
      setChecklistDocErrors((prev) => ({ ...prev, [fieldName]: 'Only JPG, PNG, or PDF files are allowed.' }));
      return;
    }

    const maxSizeBytes = CHECKLIST_DOC_MAX_SIZE_MB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setChecklistDocErrors((prev) => ({ ...prev, [fieldName]: `File is too large — max ${CHECKLIST_DOC_MAX_SIZE_MB}MB.` }));
      return;
    }

    try {
      setChecklistDocUploading(fieldName);
      const uploadedUrl = await handleImageUpload(file, 'registration-documents/', file.type || 'application/octet-stream');
      handleInputChange(fieldName, uploadedUrl);
      handleInputChange(`${fieldName}_name`, file.name);
    } catch (error) {
      console.error('Checklist document upload failed:', error);
      setChecklistDocErrors((prev) => ({ ...prev, [fieldName]: 'Upload failed — please try again.' }));
    } finally {
      setChecklistDocUploading(null);
    }
  };

  const handleChecklistDocumentRemove = (fieldName) => {
    handleInputChange(fieldName, '');
    handleInputChange(`${fieldName}_name`, '');
    setChecklistDocErrors((prev) => ({ ...prev, [fieldName]: null }));
  };

  // Handle contract clause checkbox change
  const handleContractClauseChange = (index, checked) => {
    if (checked) {
      // Clauses must be signed in order - matches the mobile app
      // (RegistrationPacketScreenNew.js's handleClauseSignaturePress),
      // which blocks signing clause N until N-1 is signed. This had no
      // equivalent guard on web at all, so a parent could sign clause 13
      // first and clause 1 last, or skip clauses entirely.
      if (index > 1 && !formData[`contract_initial_${index - 1}`]) {
        Swal.fire({
          icon: 'warning',
          title: 'Sign in order',
          text: `Please sign clause ${index - 1} before signing clause ${index}.`
        });
        return;
      }
      // Open signature modal when checkbox is checked
      setCurrentClauseIndex(index);
      setShowSignatureModal(true);
    } else {
      // Uncheck and clear signature
      handleInputChange(`contract_initial_${index}`, false);
      handleInputChange(`contract_signature_${index}`, '');
    }
  };

  // Save signature from modal
  const handleSaveSignature = async () => {
    if (!contractClauseSignatureRef.current || contractClauseSignatureRef.current.isEmpty()) {
      Swal.fire({
        icon: 'warning',
        title: 'Signature Required',
        text: 'Please provide your signature before saving.'
      });
      return;
    }

    try {
      setSignatureUploading(true);
      const signatureDataURL = contractClauseSignatureRef.current.toDataURL('image/png');
      const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
      const signatureFile = new File([signatureBlob], `contract_signature_${currentClauseIndex}_${Date.now()}.png`, { type: 'image/png' });
      const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');

      // Save signature and check the checkbox
      handleInputChange(`contract_signature_${currentClauseIndex}`, signatureUrl);
      handleInputChange(`contract_initial_${currentClauseIndex}`, true);

      setShowSignatureModal(false);
      setCurrentClauseIndex(null);

      Swal.fire({
        icon: 'success',
        title: 'Signature Saved',
        text: `Clause ${currentClauseIndex} signature saved successfully.`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Error',
        text: 'Failed to upload signature. Please try again.'
      });
    } finally {
      setSignatureUploading(false);
    }
  };

  // Close modal without saving
  const handleCloseSignatureModal = () => {
    setShowSignatureModal(false);
    setCurrentClauseIndex(null);
  };

  const focusInvalidField = (fieldKey) => {
    const selectorMap = {
      child_first_name: '#child_first_name',
      child_last_name: '#child_last_name',
      child_dob: '#child_dob',
      child_address: '#child_address',
      child_city: '#child_city',
      child_postal_code: '#child_postal_code',
      parent1_name: '#parent1_name',
      parent1_phone: '#parent1_phone',
      parent1_email: '#parent1_email',
      media_release_parent_name: '#media_release_parent_name',
      media_release_date: '#media_release_date',
      offsite_parent_name: '#offsite_parent_name',
      offsite_date: '#offsite_date',
      start_date: '#start_date',
      allergy_plan_symptoms: '#allergy_plan_symptoms',
      allergy_plan_emergency_treatment_procedures: '#allergy_plan_emergency_treatment_procedures',
      allergy_plan_signature_date: '#allergy_plan_signature_date',
      lunch_waiver_reason: '#lunch_waiver_reason',
      lunch_waiver_signature_date: '#lunch_waiver_signature_date',
      peanut_policy_acknowledge: '#peanut_policy_acknowledge',
      peanut_policy_signature_date: '#peanut_policy_signature_date'
    };

    const signatureCanvasMap = {
      media_release_signature: mediaReleaseSignaturePadRef.current,
      offsite_signature: offsiteSignaturePadRef.current,
      allergy_plan_signature: allergyPlanSignaturePadRef.current,
      lunch_waiver_signature: lunchWaiverSignaturePadRef.current,
      peanut_policy_signature: peanutPolicySignaturePadRef.current
    };

    const signatureRef = signatureCanvasMap[fieldKey];
    if (signatureRef && signatureRef.getCanvas) {
      const canvasEl = signatureRef.getCanvas();
      if (canvasEl) {
        canvasEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          try {
            canvasEl.focus();
          } catch (e) {
            // Canvas may not always be focusable depending on browser
          }
        }, 200);
        return;
      }
    }

    const selector = selectorMap[fieldKey];
    if (!selector) return;

    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        try {
          element.focus();
        } catch (e) {
          // Ignore if element cannot be focused
        }
      }, 200);
    }
  };

  // Builds the registration-package/store payload from a formData-shaped
  // object - submitData during a real submit (below), or plain formData for
  // the background draft autosave effect further down. Both carry the same
  // field names, so this one field-name-mapping function covers both
  // instead of the mapping drifting between two hand-maintained copies.
  const buildRegistrationApiPayload = (data, { id, status } = {}) => {
    const formattedChildDob = formatDateForAPI(data.child_dob);
    const derivedChecklistForSubmit = computeDerivedChecklist(data, checklistActiveRecords);
    return {
      ...(id !== undefined && id !== null && { id }),
      student_id: getEffectiveStudentId(profileData),
      ...(status !== undefined && { status }),

      // Child Information
      child_first_name: (data.child_first_name || '').trim(),
      child_last_name: (data.child_last_name || '').trim(),
      child_date_of_birth: formattedChildDob,
      child_dob: formattedChildDob,
      child_address: (data.child_address || '').trim(),
      child_city: (data.child_city || '').trim(),
      child_postal_code: (data.child_postal_code || '').trim(),

      // Mother Information (Parent 1)
      mother_name: `${(data.parent1_first_name || '').trim()} ${(data.parent1_last_name || '').trim()}`.trim(),
      mother_home_phone: (data.parent1_phone || '').trim(),
      mother_cell: (data.parent1_cell || '').trim(),
      mother_home_address: (data.parent1_address || '').trim(),
      mother_city: (data.parent1_city || '').trim(),
      mother_postal_code: (data.parent1_zip || '').trim(),
      mother_work_phone: (data.parent1_work_phone || '').trim(),
      mother_work_address: (data.parent1_work_address || '').trim(),
      mother_work_city: (data.parent1_work_city || '').trim(),
      mother_work_postal_code: (data.parent1_work_postal_code || '').trim(),
      mother_email: (data.parent1_email || '').trim(),

      // Father Information (Parent 2)
      father_name: `${(data.parent2_first_name || '').trim()} ${(data.parent2_last_name || '').trim()}`.trim(),
      father_home_phone: (data.parent2_phone || '').trim(),
      father_cell: (data.parent2_cell || '').trim(),
      father_home_address: (data.parent2_address || '').trim(),
      father_city: (data.parent2_city || '').trim(),
      father_postal_code: (data.parent2_zip || '').trim(),
      father_work_phone: (data.parent2_work_phone || '').trim(),
      father_work_address: (data.parent2_work_address || '').trim(),
      father_work_city: (data.parent2_work_city || '').trim(),
      father_work_postal_code: (data.parent2_work_postal_code || '').trim(),
      father_email: (data.parent2_email || '').trim(),

      // Emergency Contact
      emergency_contact_name: (data.emergency_contact_name || '').trim(),
      emergency_contact_relationship: (data.emergency_contact_relation || '').trim(),
      emergency_contact_home_address: (data.emergency_contact_address || '').trim(),
      emergency_contact_city: (data.emergency_contact_city || '').trim(),
      emergency_contact_postal_code: (data.emergency_contact_postal_code || '').trim(),
      emergency_contact_phone: (data.emergency_contact_phone || '').trim(),
      emergency_contact_cell: (data.emergency_contact_cell || '').trim(),
      emergency_contact_work_phone: (data.emergency_contact_work_phone || '').trim(),
      emergency_contact_work_address: (data.emergency_contact_work_address || '').trim(),
      emergency_contact_email: (data.emergency_contact_email || '').trim(),

      // Authorized Pickup Persons (map to API format)
      pickup_person_1_name: (data.pickup1_name || '').trim(),
      pickup_person_1_phone: (data.pickup1_phone || '').trim(),
      pickup_person_1_cell: (data.pickup1_cell || '').trim(),
      pickup_person_1_address: (data.pickup1_address || '').trim(),
      pickup_person_1_city: (data.pickup1_city || '').trim(),
      pickup_person_1_postal_code: (data.pickup1_postal_code || '').trim(),

      pickup_person_2_name: data.pickup2_name || '',
      pickup_person_2_phone: data.pickup2_phone || '',
      pickup_person_2_cell: data.pickup2_cell || '',
      pickup_person_2_address: data.pickup2_address || '',
      pickup_person_2_city: data.pickup2_city || '',
      pickup_person_2_postal_code: data.pickup2_postal_code || '',

      pickup_person_3_name: data.pickup3_name || '',
      pickup_person_3_phone: data.pickup3_phone || '',
      pickup_person_3_cell: data.pickup3_cell || '',
      pickup_person_3_address: data.pickup3_address || '',
      pickup_person_3_city: data.pickup3_city || '',
      pickup_person_3_postal_code: data.pickup3_postal_code || '',

      pickup_person_4_name: data.pickup4_name || '',
      pickup_person_4_phone: data.pickup4_phone || '',
      pickup_person_4_cell: data.pickup4_cell || '',
      pickup_person_4_address: data.pickup4_address || '',
      pickup_person_4_city: data.pickup4_city || '',
      pickup_person_4_postal_code: data.pickup4_postal_code || '',

      // Drop off / Pick up times
      drop_off_time: data.dropoff_time || '',
      pick_up_time: data.pickup_time || '',
      start_date: formatDateForAPI(data.start_date),
      withdrawal_date: formatDateForAPI(data.withdrawal_date),

      // Medical Information
      family_physician_name: data.family_physician_name || '',
      physician_address: data.physician_address || '',
      physician_phone_number: data.physician_phone || '',
      current_medications: data.current_medications || '',
      allergies_health_info: data.health_allergies || '',
      sleep_time_night: data.sleep_time || '',
      wake_time_morning: data.wake_time || '',
      child_favourite_activity: data.favourite_activity || '',

      // Child Information Page
      child_related_agencies_involvement: data.agency_involvement || '',
      vision_history_glasses: data.vision_history || '',
      hearing_concerns_audiologist: data.hearing_concerns || '',
      diagnoses_adhd_delays: data.diagnoses || '',
      communicable_disease_history: data.communicable_disease || '',
      eating_appetite_concerns: data.eating_concerns || '',
      developmental_concerns: data.developmental_concerns || '',
      pets_at_home: data.pets_at_home || '',
      siblings_family_info: data.siblings_info || '',
      child_favourite_toy_activity: data.favourite_toy || '',

      // Emergency Medical Authorization
      emergency_auth_parent_guardian: data.emergency_auth_parent_name || '',
      emergency_auth_signature: data.emergency_auth_signature || '',
      emergency_auth_date: formatDateForAPI(data.emergency_auth_date),

      // Media Release fields
      media_release_agree: data.media_release_agree ? 1 : 0,
      media_release_child_name_agree: data.media_release_agree ? data.media_release_child_name : '',
      media_release_date_agree: data.media_release_agree ? formatDateForAPI(data.media_release_date) : '',
      media_release_parent_name_agree: data.media_release_agree ? data.media_release_parent_name : '',
      media_release_signature_agree: data.media_release_agree ? data.media_release_signature : '',
      media_release_child_name_disagree: !data.media_release_agree ? data.media_release_child_name : '',
      media_release_date_disagree: !data.media_release_agree ? formatDateForAPI(data.media_release_date) : '',
      media_release_parent_name_disagree: !data.media_release_agree ? data.media_release_parent_name : '',
      media_release_signature_disagree: !data.media_release_agree ? data.media_release_signature : '',

      // Off-Site Permission
      offsite_permission: data.offsite_agree ? 1 : 0,
      offsite_permission_child_name: (data.offsite_child_name || '').trim(),
      offsite_permission_parent_name: (data.offsite_parent_name || '').trim(),
      offsite_permission_signature: data.offsite_signature || '',
      offsite_permission_date: formatDateForAPI(data.offsite_date),

      // Contract Clauses (1-13) - Send signature URL in contract_clause_X_initial if signature exists, otherwise send 1/0
      contract_clause_1_initial: data.contract_signature_1 ? data.contract_signature_1 : (data.contract_initial_1 ? 1 : 0),
      contract_clause_2_initial: data.contract_signature_2 ? data.contract_signature_2 : (data.contract_initial_2 ? 1 : 0),
      contract_clause_3_initial: data.contract_signature_3 ? data.contract_signature_3 : (data.contract_initial_3 ? 1 : 0),
      contract_clause_4_initial: data.contract_signature_4 ? data.contract_signature_4 : (data.contract_initial_4 ? 1 : 0),
      contract_clause_5_initial: data.contract_signature_5 ? data.contract_signature_5 : (data.contract_initial_5 ? 1 : 0),
      contract_clause_6_initial: data.contract_signature_6 ? data.contract_signature_6 : (data.contract_initial_6 ? 1 : 0),
      contract_clause_7_initial: data.contract_signature_7 ? data.contract_signature_7 : (data.contract_initial_7 ? 1 : 0),
      contract_clause_8_initial: data.contract_signature_8 ? data.contract_signature_8 : (data.contract_initial_8 ? 1 : 0),
      contract_clause_9_initial: data.contract_signature_9 ? data.contract_signature_9 : (data.contract_initial_9 ? 1 : 0),
      contract_clause_10_initial: data.contract_signature_10 ? data.contract_signature_10 : (data.contract_initial_10 ? 1 : 0),
      contract_clause_11_initial: data.contract_signature_11 ? data.contract_signature_11 : (data.contract_initial_11 ? 1 : 0),
      contract_clause_12_initial: data.contract_signature_12 ? data.contract_signature_12 : (data.contract_initial_12 ? 1 : 0),
      contract_clause_13_initial: data.contract_signature_13 ? data.contract_signature_13 : (data.contract_initial_13 ? 1 : 0),

      // Contract Clause Signatures (1-13) - Also send in separate fields for compatibility
      contract_signature_1: data.contract_signature_1 || '',
      contract_signature_2: data.contract_signature_2 || '',
      contract_signature_3: data.contract_signature_3 || '',
      contract_signature_4: data.contract_signature_4 || '',
      contract_signature_5: data.contract_signature_5 || '',
      contract_signature_6: data.contract_signature_6 || '',
      contract_signature_7: data.contract_signature_7 || '',
      contract_signature_8: data.contract_signature_8 || '',
      contract_signature_9: data.contract_signature_9 || '',
      contract_signature_10: data.contract_signature_10 || '',
      contract_signature_11: data.contract_signature_11 || '',
      contract_signature_12: data.contract_signature_12 || '',
      contract_signature_13: data.contract_signature_13 || '',

      // Contract Parent/Guardian Signature
      contract_parent_print_name: data.parent_print_name || '',
      contract_parent_signature: data.parent_signature || '',
      contract_parent_date: formatDateForAPI(data.parent_signature_date),

      // Contract Financial Party Signature
      financial_party_print_name: data.financial_print_name || '',
      financial_party_signature: data.financial_signature || '',
      financial_party_date: formatDateForAPI(data.financial_signature_date),
      financial_party_agency: data.financial_agency || '',

      // Arrival & Departure Signatures
      parent_guardian_signature: data.parent_guardian_signature || '',
      executive_director_signature: data.executive_director_signature || '',

      // Registration Checklist - always recomputed from the authoritative
      // source fields at submit time (never trust a cached checklist_*
      // value on data, which may be stale - see computeDerivedChecklist).
      checklist_registration_form: derivedChecklistForSubmit.checklist_registration_form ? 1 : 0,
      checklist_emergency_form: derivedChecklistForSubmit.checklist_emergency_form ? 1 : 0,
      checklist_health_forms: derivedChecklistForSubmit.checklist_health_forms ? 1 : 0,
      checklist_birth_certificate: derivedChecklistForSubmit.checklist_birth_certificate ? 1 : 0,
      checklist_parent_handbook: derivedChecklistForSubmit.checklist_parent_handbook ? 1 : 0,
      checklist_waivers: derivedChecklistForSubmit.checklist_waivers ? 1 : 0,
      checklist_child_info_page: derivedChecklistForSubmit.checklist_child_info_page ? 1 : 0,
      checklist_signed_contract: derivedChecklistForSubmit.checklist_signed_contract ? 1 : 0,
      checklist_offsite_permission: derivedChecklistForSubmit.checklist_offsite_form ? 1 : 0,
      checklist_medical_reports: derivedChecklistForSubmit.checklist_medical_reports ? 1 : 0,
      checklist_pre_authorized_debit: derivedChecklistForSubmit.checklist_debit_form ? 1 : 0,
      checklist_all_about_me: derivedChecklistForSubmit.checklist_all_about_me ? 1 : 0,
      checklist_meal_waiver: derivedChecklistForSubmit.checklist_meal_waiver ? 1 : 0,
      checklist_parent_signature: data.parent_checklist_signature || '',
      checklist_parent_date: formatDateForAPI(data.parent_checklist_date),
      checklist_staff_signature: data.staff_witness_signature || '',
      checklist_staff_date: formatDateForAPI(data.checklist_staff_date),

      // Checklist-backing documents - uploaded to S3 via handleImageUpload
      // (see handleChecklistDocumentUpload) and the returned URL kept in
      // formData under the internal field names used to wire up each
      // upload widget (health_immunization_document / medical_reports_document);
      // the backend's own field names for these 3 are
      // health_form_document / medical_report_document /
      // birth_certificate_document, so that's what's sent here.
      birth_certificate_document: data.birth_certificate_document || '',
      health_form_document: data.health_immunization_document || '',
      medical_report_document: data.medical_reports_document || '',
      waivers_document: data.waivers_document || '',
      meal_waiver_document: data.meal_waiver_document || '',
      birth_certificate_document_name: data.birth_certificate_document_name || '',
      health_form_document_name: data.health_immunization_document_name || '',
      medical_report_document_name: data.medical_reports_document_name || '',
      waivers_document_name: data.waivers_document_name || '',
      meal_waiver_document_name: data.meal_waiver_document_name || ''
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // STEP 0: Sync address inputs from DOM before validation
    // This ensures any manually typed/pasted addresses are captured without state lag
    const syncedFormData = { ...formData };
    const addressFieldIds = [
      'child_address', 'parent1_address', 'parent2_address',
      'parent1_work_address', 'parent2_work_address',
      'emergency_contact_address', 'emergency_contact_work_address',
      'physician_address', 'pickup1_address', 'pickup2_address',
      'pickup3_address', 'pickup4_address'
    ];
    
    addressFieldIds.forEach(fieldId => {
      const input = document.querySelector(`input[id="${fieldId}"]`);
      if (input && input.value && input.value.trim()) {
        syncedFormData[fieldId] = input.value.trim();
      }
    });

    // Use synced data for validation instead of stale formData
    const validationErrors = [];
    const addError = (fieldName, message) => {
      validationErrors.push({ field: fieldName, message });
    };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // ===== CHILD INFORMATION - Required fields =====
    if (!syncedFormData.child_first_name?.trim()) {
      addError('child_first_name', '👶 Child: First name is required');
    }
    if (!syncedFormData.child_last_name?.trim()) {
      addError('child_last_name', '👶 Child: Last name is required');
    }
    if (!syncedFormData.child_dob) {
      addError('child_dob', '👶 Child: Date of birth is required');
    }
    if (!syncedFormData.child_address || (typeof syncedFormData.child_address === 'string' && !syncedFormData.child_address.trim())) {
      addError('child_address', '👶 Child: Address is required');
    }
    if (!syncedFormData.child_city?.trim()) {
      addError('child_city', '👶 Child: City is required');
    }
    if (!syncedFormData.child_postal_code?.trim()) {
      addError('child_postal_code', '👶 Child: Postal/ZIP code is required');
    }

    // ===== PARENT/GUARDIAN 1 INFORMATION - Required fields =====
    const parent1FullName = `${syncedFormData.parent1_first_name || ''} ${syncedFormData.parent1_last_name || ''}`.trim();
    if (!parent1FullName) {
      addError('parent1_name', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Name is required');
    }
    if (!syncedFormData.parent1_phone?.trim()) {
      addError('parent1_phone', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Phone number is required');
    } else {
      const parent1AllDigits = syncedFormData.parent1_phone.replace(/\D/g, '');
      if (parent1AllDigits.length < 10) {
        addError('parent1_phone', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Phone must have at least 10 digits');
      }
    }
    if (!syncedFormData.parent1_email?.trim()) {
      addError('parent1_email', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Email is required');
    } else if (!emailRegex.test(syncedFormData.parent1_email)) {
      addError('parent1_email', '👨‍👩‍👧 Parent/Guardian 1 (Mother): Please enter a valid email address');
    }

    // ===== OPTIONAL PARENT 2 EMAIL VALIDATION =====
    if (syncedFormData.parent2_email?.trim() && !emailRegex.test(syncedFormData.parent2_email)) {
      addError('parent2_email', '👨‍👩‍👦 Parent/Guardian 2 (Father): Please enter a valid email address');
    }

    // ===== OPTIONAL EMERGENCY CONTACT EMAIL VALIDATION =====
    if (syncedFormData.emergency_contact_email?.trim() && !emailRegex.test(syncedFormData.emergency_contact_email)) {
      addError('emergency_contact_email', '🆘 Emergency Contact: Please enter a valid email address');
    }

    // ===== MEDIA RELEASE VALIDATION =====
    if (syncedFormData.media_release_agree) {
      if (!syncedFormData.media_release_parent_name?.trim()) {
        addError('media_release_parent_name', '📄 Media Release: Parent/guardian name is required');
      }
      if (!syncedFormData.media_release_date) {
        addError('media_release_date', '📄 Media Release: Date is required');
      }
      const hasMediaReleaseSignature = syncedFormData.media_release_signature || 
        (mediaReleaseSignaturePadRef.current && !mediaReleaseSignaturePadRef.current.isEmpty());
      if (!hasMediaReleaseSignature) {
        addError('media_release_signature', '📄 Media Release: Signature is required');
      }
    }

    // ===== OFFSITE PERMISSION VALIDATION =====
    if (syncedFormData.offsite_agree) {
      if (!syncedFormData.offsite_parent_name?.trim()) {
        addError('offsite_parent_name', '🚌 Off-Site Permission: Parent/guardian name is required');
      }
      if (!syncedFormData.offsite_date) {
        addError('offsite_date', '🚌 Off-Site Permission: Date is required');
      }
      const hasOffsiteSignature = syncedFormData.offsite_signature || 
        (offsiteSignaturePadRef.current && !offsiteSignaturePadRef.current.isEmpty());
      if (!hasOffsiteSignature) {
        addError('offsite_signature', '🚌 Off-Site Permission: Signature is required');
      }
    }

    // ===== ALLERGY ACTION PLAN VALIDATION (whenever its fields are showing) =====
    if (allergyPlanFieldsVisible) {
      if (!syncedFormData.allergy_plan_symptoms?.trim()) {
        addError('allergy_plan_symptoms', '🚨 Allergy Action Plan: Symptoms are required');
      }
      if (!syncedFormData.allergy_plan_emergency_treatment_procedures?.trim()) {
        addError('allergy_plan_emergency_treatment_procedures', '🚨 Allergy Action Plan: Emergency treatment procedures are required');
      }
      const hasAllergyPlanSignature = syncedFormData.allergy_plan_signature ||
        (allergyPlanSignaturePadRef.current && !allergyPlanSignaturePadRef.current.isEmpty());
      if (!hasAllergyPlanSignature) {
        addError('allergy_plan_signature', '🚨 Allergy Action Plan: Signature is required');
      }
      if (!syncedFormData.allergy_plan_signature_date) {
        addError('allergy_plan_signature_date', '🚨 Allergy Action Plan: Signature date is required');
      }
    }

    // ===== LUNCH PLAN WAIVER VALIDATION (whenever its fields are showing) =====
    if (lunchWaiverFieldsVisible) {
      if (!syncedFormData.lunch_waiver_reason?.trim()) {
        addError('lunch_waiver_reason', '🍽️ Lunch Plan Waiver: Reason is required');
      }
      const hasLunchWaiverSignature = syncedFormData.lunch_waiver_signature ||
        (lunchWaiverSignaturePadRef.current && !lunchWaiverSignaturePadRef.current.isEmpty());
      if (!hasLunchWaiverSignature) {
        addError('lunch_waiver_signature', '🍽️ Lunch Plan Waiver: Signature is required');
      }
      if (!syncedFormData.lunch_waiver_signature_date) {
        addError('lunch_waiver_signature_date', '🍽️ Lunch Plan Waiver: Signature date is required');
      }
    }

    // ===== NO PEANUT POLICY VALIDATION - always required, fields always showing =====
    if (!syncedFormData.peanut_policy_acknowledge) {
      addError('peanut_policy_acknowledge', '🥜 No Peanut Policy: Please acknowledge the policy');
    }
    {
      const hasPeanutPolicySignature = syncedFormData.peanut_policy_signature ||
        (peanutPolicySignaturePadRef.current && !peanutPolicySignaturePadRef.current.isEmpty());
      if (!hasPeanutPolicySignature) {
        addError('peanut_policy_signature', '🥜 No Peanut Policy: Signature is required');
      }
    }
    if (!syncedFormData.peanut_policy_signature_date) {
      addError('peanut_policy_signature_date', '🥜 No Peanut Policy: Signature date is required');
    }

    // ===== ARRIVAL / EMERGENCY MEDICAL / CHECKLIST SIGNATURES =====
    // Each is shown with a required "*" in the form but was never actually
    // checked here, so a registration could submit with any of them blank.
    const hasArrivalSignature = syncedFormData.parent_guardian_signature ||
      (arrivalSignaturePadRef.current && !arrivalSignaturePadRef.current.isEmpty());
    if (!hasArrivalSignature) {
      addError('parent_guardian_signature', '🕐 Arrival and Departure: Parent/Guardian signature is required');
    }
    const hasEmergencyAuthSignature = syncedFormData.emergency_auth_signature ||
      (emergencyMedicalSignaturePadRef.current && !emergencyMedicalSignaturePadRef.current.isEmpty());
    if (!hasEmergencyAuthSignature) {
      addError('emergency_auth_signature', '🏥 Emergency Medical Authorization: Signature is required');
    }
    const hasChecklistSignature = syncedFormData.parent_checklist_signature ||
      (checklistSignaturePadRef.current && !checklistSignaturePadRef.current.isEmpty());
    if (!hasChecklistSignature) {
      addError('parent_checklist_signature', '✅ Registration Checklist: Parent signature is required');
    }

    // ===== CONTRACT TERMS & CONDITIONS VALIDATION =====
    // All 13 clauses, the printed name and the signature are shown as
    // required (the section header and each field carry a red "*") but
    // nothing enforced that at submit - a registration could go through
    // with 0/13 clauses signed and no contract signature at all. Matches
    // the mobile app's equivalent check (RegistrationPacketScreenNew.js).
    const signedClauseCount = Object.keys(syncedFormData)
      .filter((key) => key.startsWith('contract_initial_') && syncedFormData[key]).length;
    if (signedClauseCount < 13) {
      addError('contract_initial_1', '📜 Contract Terms & Conditions: All 13 clauses must be signed');
    }
    if (!syncedFormData.parent_print_name?.trim()) {
      addError('parent_print_name', '📜 Contract Terms & Conditions: Parent/Guardian printed name is required');
    }
    const hasContractSignature = syncedFormData.parent_signature ||
      (contractParentSignaturePadRef.current && !contractParentSignaturePadRef.current.isEmpty());
    if (!hasContractSignature) {
      addError('parent_signature', '📜 Contract Terms & Conditions: Signature is required');
    }
    if (!syncedFormData.parent_signature_date) {
      addError('parent_signature_date', '📜 Contract Terms & Conditions: Signature date is required');
    }

    // ===== REQUIRED DOCUMENT UPLOADS =====
    // Medical/Assessment Reports is deliberately left out - its own card
    // says "If any are applicable" (optional), matching the mobile app's
    // equivalent rule, even though it also carries a required=true prop.
    if (!syncedFormData.health_immunization_document) {
      addError('upload-health-forms', '📁 Required Document Uploads: Health Forms must be uploaded');
    }
    if (!syncedFormData.birth_certificate_document) {
      addError('upload-birth-cert', '📁 Required Document Uploads: Birth Certificate must be uploaded');
    }

    // ===== ENROLLMENT START DATE =====
    if (!syncedFormData.start_date) {
      addError('start_date', '📅 Enrollment: Start date is required');
    }

    // Check if there are validation errors
    if (validationErrors.length > 0) {
      // Show all errors at once (like the original)
      const errorHTML = `
        <div style="text-align: left; max-height: 400px; overflow-y: auto;">
          <div style="font-weight: bold; margin-bottom: 15px; font-size: 16px;">
            Found ${validationErrors.length} validation error${validationErrors.length !== 1 ? 's' : ''}:
          </div>
          <ul style="list-style: none; padding: 0; margin: 0;">
            ${validationErrors.map(error => `
              <li style="
                padding: 10px; 
                margin: 5px 0; 
                background-color: #fff3cd; 
                border-left: 4px solid #ffc107; 
                border-radius: 4px;
                font-size: 14px;
              ">
                ⚠️ ${error.message}
              </li>
            `).join('')}
          </ul>
        </div>
      `;

      Swal.fire({
        icon: 'error',
        title: 'Form Validation Errors',
        html: errorHTML,
        width: '600px',
        confirmButtonText: 'Fix Errors',
        confirmButtonColor: '#dc3545',
        didClose: () => {
          const firstError = validationErrors[0];
          if (firstError) {
            const field = document.querySelector(`#${firstError.field}`);
            if (field) {
              setTimeout(() => field.focus(), 100);
            }
          }
        }
      });
      return;
    }

    // Show uploading loader
    Swal.fire({
      title: 'Processing...',
      html: 'Uploading signatures and preparing data...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const submitData = {
        ...syncedFormData,
        ...(isEditMode && { id })
      };

      // STEP 1: Upload all signatures first before calling store API. Each
      // call is a no-op that resolves to the existing value when its pad is
      // empty or already holds a saved http URL, and never throws even if
      // something goes wrong (a tainted canvas included) - see
      // uploadSignatureIfDrawn's own comment for why that matters. So every
      // pad can just be listed here unconditionally, instead of each
      // needing its own "is there anything to do" guard first.
      const [
        parentGuardianSignature,
        emergencyAuthSignature,
        mediaReleaseSignature,
        parentChecklistSignature,
        contractParentSignature,
        financialSignature,
        offsiteSignatureUrl,
        allergyPlanSignature,
        lunchWaiverSignature,
        peanutPolicySignature
      ] = await Promise.all([
        uploadSignatureIfDrawn(arrivalSignaturePadRef, submitData.parent_guardian_signature, 'arrival_signature'),
        uploadSignatureIfDrawn(emergencyMedicalSignaturePadRef, submitData.emergency_auth_signature, 'emergency_auth_signature'),
        uploadSignatureIfDrawn(mediaReleaseSignaturePadRef, submitData.media_release_signature, 'media_release_signature'),
        uploadSignatureIfDrawn(checklistSignaturePadRef, submitData.parent_checklist_signature, 'checklist_signature'),
        uploadSignatureIfDrawn(contractParentSignaturePadRef, submitData.parent_signature, 'contract_parent_signature'),
        uploadSignatureIfDrawn(contractFinancialSignaturePadRef, submitData.financial_signature, 'contract_financial_signature'),
        uploadSignatureIfDrawn(offsiteSignaturePadRef, submitData.offsite_signature, 'offsite_signature'),
        // Allergy Action Plan / Lunch Plan Waiver only apply while their
        // section is actually showing (opted into, or an existing record
        // being edited) - pass a null padRef otherwise so the pad's
        // current value (possibly leftover from a still-open, unrelated
        // section) is never picked up. All 3 pass their own dirtyRef -
        // see uploadSignatureIfDrawn and the refs' own declaration for why
        // these 3 specifically need that rather than the default heuristic.
        allergyPlanFieldsVisible
          ? uploadSignatureIfDrawn(allergyPlanSignaturePadRef, submitData.allergy_plan_signature, 'allergy_plan_signature', allergyPlanSignatureDirtyRef)
          : submitData.allergy_plan_signature,
        lunchWaiverFieldsVisible
          ? uploadSignatureIfDrawn(lunchWaiverSignaturePadRef, submitData.lunch_waiver_signature, 'lunch_waiver_signature', lunchWaiverSignatureDirtyRef)
          : submitData.lunch_waiver_signature,
        // No Peanut Policy's fields are always showing (no opt-in gate).
        uploadSignatureIfDrawn(peanutPolicySignaturePadRef, submitData.peanut_policy_signature, 'peanut_policy_signature', peanutPolicySignatureDirtyRef)
      ]);

      submitData.parent_guardian_signature = parentGuardianSignature;
      submitData.emergency_auth_signature = emergencyAuthSignature;
      submitData.media_release_signature = mediaReleaseSignature;
      submitData.parent_checklist_signature = parentChecklistSignature;
      submitData.parent_signature = contractParentSignature;
      submitData.financial_signature = financialSignature;
      submitData.offsite_signature = offsiteSignatureUrl;
      submitData.allergy_plan_signature = allergyPlanSignature;
      submitData.lunch_waiver_signature = lunchWaiverSignature;
      submitData.peanut_policy_signature = peanutPolicySignature;

      // Map form fields to API expected field names
      const apiPayload = buildRegistrationApiPayload(submitData, {
        id: isEditMode ? submitData.id : undefined,
        status: 1
      });

      console.log('Submitting Registration - Full Child Address Data:', {
        child_address: apiPayload.child_address,
        child_city: apiPayload.child_city,
        child_postal_code: apiPayload.child_postal_code,
        formData_child_address: formData.child_address,
        submitData_child_address: submitData.child_address
      });

      console.log('Full API Payload - Child Section:', {
        child_first_name: apiPayload.child_first_name,
        child_last_name: apiPayload.child_last_name,
        child_date_of_birth: apiPayload.child_date_of_birth,
        child_address: apiPayload.child_address,
        child_city: apiPayload.child_city,
        child_postal_code: apiPayload.child_postal_code
      });

      // STEP 2: Update loader message
      Swal.update({
        html: 'Submitting registration data...'
      });

      // STEP 3: Call store API with all uploaded signature URLs
      const result = await dispatch(submitChildRegistration(apiPayload)).unwrap();

      // Log the response from backend
      console.log('Backend Response after save:', result);
      console.log('Backend Response - Child Address:', {
        child_address: result?.data?.child_address || result?.child_address,
        child_home_address: result?.data?.child_home_address || result?.child_home_address,
        response_keys: result?.data ? Object.keys(result.data) : Object.keys(result || {})
      });

      // STEP 3b: Save the Allergy Action Plan / Lunch Plan Waiver / No
      // Peanut Policy sections to their own dedicated endpoints - these are
      // separate backend records from the child registration above (the
      // same ones the Registration Packet tab's own steps manage), not
      // fields on it. Only attempted for a section that doesn't already
      // have an active record and (for the two opt-in sections) was
      // actually opted into. Each is independent and non-fatal: one failing
      // doesn't roll back the child registration that already succeeded,
      // or block the other two - failures are just surfaced afterward.
      const studentId = getEffectiveStudentId(profileData);
      const additionalSubmissions = [];

      if (allergyPlanFieldsVisible) {
        additionalSubmissions.push(
          dispatch(submitAllergyActionPlan({
            // Present only when editing an existing record (allergy_plan_id
            // is only ever set by the pre-fill effect above) - omitting it
            // for a brand-new plan lets the backend create rather than
            // update. submitAllergyActionPlan's thunk drops null/undefined
            // keys before building the FormData it sends.
            id: submitData.allergy_plan_id || undefined,
            is_peanuts: submitData.allergy_plan_is_peanuts,
            is_shellfish: submitData.allergy_plan_is_shellfish,
            shellfish_specify: submitData.allergy_plan_shellfish_specify,
            is_tree_nuts: submitData.allergy_plan_is_tree_nuts,
            is_fish: submitData.allergy_plan_is_fish,
            fish_specify: submitData.allergy_plan_fish_specify,
            is_milk: submitData.allergy_plan_is_milk,
            is_food_additives: submitData.allergy_plan_is_food_additives,
            food_additives_specify: submitData.allergy_plan_food_additives_specify,
            all_dairy_products: submitData.allergy_plan_all_dairy_products,
            insect_stings_bites: submitData.allergy_plan_insect_stings_bites,
            insect_stings_bites_specify: submitData.allergy_plan_insect_stings_bites_specify,
            insect_stings_bites_medication: submitData.allergy_plan_insect_stings_bites_medication,
            is_other: submitData.allergy_plan_is_other,
            other_specify: submitData.allergy_plan_other_specify,
            symptoms: submitData.allergy_plan_symptoms,
            emergency_treatment_procedures: submitData.allergy_plan_emergency_treatment_procedures,
            require_epi_pen: submitData.allergy_plan_require_epi_pen,
            location_of_epi_pen: submitData.allergy_plan_location_of_epi_pen,
            is_allergy_form: submitData.allergy_plan_is_allergy_form,
            is_preference_form: submitData.allergy_plan_is_preference_form,
            parent_signature: submitData.allergy_plan_signature,
            parent_signature_date: formatDateForAPI(submitData.allergy_plan_signature_date),
            student_id: studentId
          })).unwrap().then(() => null).catch(() => 'Allergy Action Plan')
        );
      }

      if (lunchWaiverFieldsVisible) {
        additionalSubmissions.push(
          dispatch(submitLunchPlanWaiver({
            // Present only when editing (lunch_waiver_id is only ever set
            // by the pre-fill effect above) so the backend updates the
            // existing record instead of creating a duplicate.
            id: submitData.lunch_waiver_id || undefined,
            reason: submitData.lunch_waiver_reason,
            parents_signature: submitData.lunch_waiver_signature,
            parent_signature_date: formatDateForAPI(submitData.lunch_waiver_signature_date),
            student_id: studentId
          })).unwrap().then(() => null).catch(() => 'Lunch Plan Waiver')
        );
      }

      // No Peanut Policy's fields are always showing, so it's always
      // (re-)submitted here too - same update-vs-create id rule as above.
      additionalSubmissions.push(
        dispatch(submitNoPeanutPolicy({
          id: submitData.peanut_policy_id || undefined,
          parent_signature: submitData.peanut_policy_signature,
          parent_signature_date: formatDateForAPI(submitData.peanut_policy_signature_date),
          student_id: studentId
        })).unwrap().then(() => null).catch(() => 'No Peanut Policy')
      );

      const failedSections = additionalSubmissions.length > 0
        ? (await Promise.all(additionalSubmissions)).filter(Boolean)
        : [];

      // Re-fetch the 3 sections' own lists so fetchedActiveAllergyPlan /
      // fetchedActiveLunchWaiver / fetchedActiveNoPeanutPolicy (and the
      // merge built from them - see activeAllergyPlans etc.) reflect what
      // was just saved. Without this, if the form stays mounted after a
      // successful save (the Registration Packet tab keeps showing this
      // Form, rather than switching away, until the *whole* packet is
      // complete), those 3 stay on whatever was fetched at mount time -
      // stale, pre-save data that the merge prefers over the fresher
      // embedded-profile copy - so a save that genuinely succeeded could
      // still appear to have been silently discarded once the pre-fill
      // effects re-run against still-stale data.
      if (studentId) {
        dispatch(fetchAllergyActionPlans({ page: 1, limit: 20, student_id: studentId }));
        dispatch(fetchLunchPlanWaivers({ page: 1, limit: 20, student_id: studentId }));
        dispatch(fetchNoPeanutPolicies({ page: 1, limit: 20, student_id: studentId }));
      }

      // STEP 4: Close the loader
      Swal.close();

      // STEP 5: Show success message only on successful submission
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: isEditMode ? 'Child registration updated successfully.' : 'Child registration submitted successfully.',
        timer: 2000,
        showConfirmButton: false
      });

      // If the core registration saved but one of the 3 additional sections
      // above didn't, say so explicitly instead of letting the success
      // message imply everything went through - the user can retry just
      // that section from the Registration Packet tab.
      if (failedSections.length > 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'Some sections need another try',
          html: `Your registration was saved, but the following section${failedSections.length > 1 ? 's' : ''} could not be saved: <strong>${failedSections.join(', ')}</strong>.<br><br>Please retry ${failedSections.length > 1 ? 'them' : 'it'} from the Registration Packet tab.`,
          confirmButtonText: 'OK'
        });
      }

      // STEP 6: Clear all signature pads only after successful submission
      if (arrivalSignaturePadRef.current) arrivalSignaturePadRef.current.clear();
      if (emergencyMedicalSignaturePadRef.current) emergencyMedicalSignaturePadRef.current.clear();
      if (mediaReleaseSignaturePadRef.current) mediaReleaseSignaturePadRef.current.clear();
      if (checklistSignaturePadRef.current) checklistSignaturePadRef.current.clear();
      if (contractParentSignaturePadRef.current) contractParentSignaturePadRef.current.clear();
      if (contractFinancialSignaturePadRef.current) contractFinancialSignaturePadRef.current.clear();
      if (offsiteSignaturePadRef.current) offsiteSignaturePadRef.current.clear();
      if (allergyPlanSignaturePadRef.current) allergyPlanSignaturePadRef.current.clear();
      if (lunchWaiverSignaturePadRef.current) lunchWaiverSignaturePadRef.current.clear();
      if (peanutPolicySignaturePadRef.current) peanutPolicySignaturePadRef.current.clear();
      allergyPlanSignatureDirtyRef.current = false;
      lunchWaiverSignatureDirtyRef.current = false;
      peanutPolicySignatureDirtyRef.current = false;

      // Reset form or navigate
      if (onSubmit) {
        // Call onSubmit callback (getProfileDetails in user-profile.js) which will fetch registration data
        onSubmit(formData);
      } else {
        // Only fetch registration packages when not in profile view
        await dispatch(fetchRegistrationPackages({ page: 1, limit: 100, student_id: getEffectiveStudentId(profileData) })).unwrap();
        navigate('/dashboard/child-registration/list', { replace: true });
      }
    } catch (error) {
      // Close any open loader
      Swal.close();
      
      console.error('Form submission error:', error);
      
      // Parse validation errors if present
      let errorTitle = 'Submission Failed';
      let errorMessage = 'Failed to submit the form. Please try again.';
      let errorIcon = 'error';
      
      if (error && typeof error === 'string') {
        errorMessage = error;
      } else if (error?.errors && Array.isArray(error.errors)) {
        // Handle 422 validation errors
        errorTitle = 'Validation Error';
        errorIcon = 'warning';
        errorMessage = error.errors.map(err => err.message).join('<br>');
      } else if (error?.message) {
        errorMessage = error.message;
        // Check for specific error types
        if (error.message.includes('Network') || error.message.includes('network')) {
          errorTitle = 'Network Error';
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorTitle = 'Request Timeout';
          errorMessage = 'The request took too long to complete. Please try again.';
        } else if (error.message.includes('signature') || error.message.includes('upload')) {
          errorTitle = 'Upload Error';
          errorMessage = 'Failed to upload one or more signatures. Please check your signatures and try again.';
        }
      }
      
      Swal.fire({
        icon: errorIcon,
        title: errorTitle,
        html: errorMessage + '<br><br><small style="color: #666;">Note: Your signatures have been preserved. Please correct the errors and submit again.</small>',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
        allowOutsideClick: true,
        allowEscapeKey: true
      });
      
      // Note: Signature pads are NOT cleared on error - they remain intact for resubmission
    }
  };

  // Autosave: quietly persists progress as a draft (status: 0) once the
  // parent moves away from a field (blur), instead of a "save N seconds
  // after every keystroke" timer - that fired the store API on essentially
  // every pause while typing across a form this size, which is needless
  // server load for data nobody has finished entering yet. Attached to the
  // whole form's outer container below (not each of its many individual
  // inputs) - React's onBlur bubbles, so one listener there catches every
  // field's blur without having to wire dozens of onBlur props by hand.
  const performAutosave = async () => {
    // Nothing loaded yet (edit mode's fetch still in flight), a real submit
    // already in progress, or nothing worth saving yet.
    if (!autoSaveReadyRef.current || submitLoading) {
      return;
    }
    if (!formData.child_first_name?.trim() && !formData.child_last_name?.trim()) {
      return;
    }

    // Dirty-check: skip the round-trip entirely if nothing actually changed
    // since the last successful save (e.g. the user just tabbed through a
    // field without editing it).
    const currentSnapshot = JSON.stringify(formData);
    if (currentSnapshot === lastSavedFormDataRef.current) {
      return;
    }

    try {
      setAutoSaveStatus('saving');
      setAutoSaveError(null);

      // A record that was already fully submitted (status 1) before this
      // edit session stays submitted on autosave - it already passed the
      // "everything required is present" check to get there, so there's no
      // reason to quietly downgrade it back to a draft while someone edits
      // a couple of fields afterward. Only a genuinely new/still-incomplete
      // registration autosaves as an actual draft.
      const wasAlreadySubmitted = isEditMode && Number(formData.status) === 1;

      const draftPayload = buildRegistrationApiPayload(formData, {
        id: isEditMode ? id : (draftId || undefined),
        status: wasAlreadySubmitted ? 1 : 0
      });

      const result = await dispatch(submitChildRegistration(draftPayload)).unwrap();
      const savedId = result?.data?.id;
      if (savedId && !isEditMode && !draftId) {
        setDraftId(savedId);
      }

      lastSavedFormDataRef.current = currentSnapshot;
      setAutoSaveStatus('saved');
    } catch (err) {
      // Best-effort and silent by design (no Swal/blocking UI) - a failed
      // autosave just means the next blur (or the real Submit) tries again;
      // see the small status indicator near the Submit button.
      console.error('Draft autosave failed:', err);
      setAutoSaveStatus('error');
      setAutoSaveError(err?.message || 'Autosave failed');
    }
  };

  // Several fields (every AddressAutocomplete instance) keep their own
  // internal typed value and only sync it into formData from their OWN
  // onBlur handler - which fires as part of the very same blur event this
  // container's onBlur is bubbling up from. Calling performAutosave()
  // directly here would close over formData as of THIS render, which is a
  // tick behind: it doesn't yet include the setFormData that field's own
  // onBlur just queued, so the last few characters typed into an address
  // field before tabbing away were silently dropped from the autosave.
  // Routing the actual call through a ref - refreshed every render - means
  // by the time the debounce timer fires, it invokes whichever
  // performAutosave closure is current at that moment, formData update
  // included, not whichever one existed when the blur first happened.
  const performAutosaveRef = useRef(performAutosave);
  performAutosaveRef.current = performAutosave;

  // Debounced ~600ms so tabbing/clicking through several fields in quick
  // succession (each one blurring the last) coalesces into a single save
  // once the user actually pauses, rather than one call per field.
  const handleFormBlur = () => {
    if (blurSaveTimerRef.current) {
      clearTimeout(blurSaveTimerRef.current);
    }
    blurSaveTimerRef.current = setTimeout(() => {
      performAutosaveRef.current();
    }, 600);
  };

  useEffect(() => {
    return () => {
      if (blurSaveTimerRef.current) {
        clearTimeout(blurSaveTimerRef.current);
      }
    };
  }, []);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/dashboard/child-registration/list');
    }
  };

  const getChecklistDocIcon = (nameOrUrl = '') =>
    nameOrUrl.toLowerCase().endsWith('.pdf') ? 'picture_as_pdf' : 'image';

  const CHECKLIST_CARD_MIN_HEIGHT = 72;

  const checklistRowStyle = (status) => ({
    border: `1px solid ${status === 'complete' ? '#a8dab5' : status === 'error' ? '#f3b7ae' : '#e3e6ea'}`,
    backgroundColor: status === 'complete' ? '#f0f9f1' : status === 'error' ? '#fdf1ef' : '#fff',
    minHeight: `${CHECKLIST_CARD_MIN_HEIGHT}px`
  });

  // A checklist item that reflects data already collected elsewhere in the
  // form — always read-only, never toggled by hand. Now also used for the 3
  // document-backed items (their upload widgets live in the "Required
  // Document Uploads" section above; this row just mirrors formData via the
  // matching derivedChecklist.checklist_* flag).
  const renderAutoChecklistItem = (label, checked, id, pendingHint = 'Awaiting info above') => (
    <div
      id={id}
      className="d-flex flex-column justify-content-between p-2 rounded"
      style={checklistRowStyle(checked ? 'complete' : 'pending')}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="d-flex align-items-center gap-2">
          <i className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '18px', color: checked ? '#2e7d32' : '#c2c7cd' }}>
            {checked ? 'check_circle' : 'radio_button_unchecked'}
          </i>
          <span className={checked ? 'text-dark fw-medium' : 'text-muted'} style={{ fontSize: '13px' }}>{label}</span>
        </div>
        <Badge
          bg={checked ? 'success' : 'light'}
          className="fw-normal flex-shrink-0"
          style={!checked ? { color: '#8a8f98', border: '1px solid #dcdfe3' } : {}}
        >
          {checked ? 'Complete' : 'Pending'}
        </Badge>
      </div>
      <div className="small text-muted mt-1 ms-4">
        {checked ? 'Verified automatically' : pendingHint}
      </div>
    </div>
  );

  // The prominent upload card for a mandatory registration document (Health
  // Forms, Medical Reports, Birth Certificate) — lives in its own "Required
  // Document Uploads" section above the checklist summary. Writes to the
  // same formData field the checklist's own renderAutoChecklistItem row
  // reads via derivedChecklist.checklist_*, so that row turns green the
  // instant a file finishes uploading here — no separate state to keep in
  // sync.
  const renderDocumentUploadCard = (label, description, fieldName, id, required = false) => {
    const documentUrl = formData[fieldName];
    const fileName = formData[`${fieldName}_name`] || 'Uploaded document';
    const uploading = checklistDocUploading === fieldName;
    const error = checklistDocErrors[fieldName];
    const uploaded = !!documentUrl;

    return (
      <Col md={4} className="mb-3">
        <div
          className="h-100 d-flex flex-column p-3 rounded"
          style={{
            border: `1px solid ${uploaded ? '#a8dab5' : error ? '#f3b7ae' : '#dcdfe3'}`,
            backgroundColor: uploaded ? '#f0f9f1' : error ? '#fdf1ef' : '#fff'
          }}
        >
          <div className="d-flex align-items-start gap-2 mb-2">
            <i
              className="material-symbols-outlined flex-shrink-0"
              style={{ fontSize: '22px', color: uploaded ? '#2e7d32' : '#6f42c1' }}
            >
              {uploaded ? 'check_circle' : 'upload_file'}
            </i>
            <div>
              <div className="fw-medium text-dark" style={{ fontSize: '14px' }}>
                {label}{required && <span className="text-danger"> *</span>}
              </div>
              {description && <div className="small text-muted">{description}</div>}
            </div>
          </div>

          <div className="mt-auto">
            {uploaded ? (
              <div>
                <div className="d-flex align-items-center gap-1 small text-muted text-truncate mb-2">
                  <i className="material-symbols-outlined" style={{ fontSize: '16px' }}>{getChecklistDocIcon(fileName)}</i>
                  <span className="text-truncate">{fileName}</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="small">View</a>
                  <Form.Label htmlFor={`${id}-file`} className="small text-primary mb-0" style={{ cursor: 'pointer' }}>
                    Replace
                  </Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0 small"
                    onClick={() => handleChecklistDocumentRemove(fieldName)}
                    type="button"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Form.Label
                htmlFor={`${id}-file`}
                className="d-flex flex-column align-items-center justify-content-center text-center mb-0 w-100"
                style={{
                  cursor: uploading ? 'wait' : 'pointer',
                  border: '1px dashed #c2c7cd',
                  borderRadius: '8px',
                  padding: '14px 10px',
                  color: '#5a6c7d'
                }}
              >
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="mb-1" />
                    <span className="small">Uploading...</span>
                  </>
                ) : (
                  <>
                    <i className="material-symbols-outlined mb-1" style={{ fontSize: '20px' }}>cloud_upload</i>
                    <span className="small fw-medium">Click to upload</span>
                    <span className="small text-muted">JPG, PNG or PDF · max {CHECKLIST_DOC_MAX_SIZE_MB}MB</span>
                  </>
                )}
              </Form.Label>
            )}
            <Form.Control
              id={`${id}-file`}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              className="d-none"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                handleChecklistDocumentUpload(fieldName, file);
              }}
            />
            {error && (
              <div className="text-danger small mt-2 d-flex align-items-center gap-1">
                <i className="material-symbols-outlined" style={{ fontSize: '14px' }}>error</i>
                {error}
              </div>
            )}
          </div>
        </div>
      </Col>
    );
  };

  // Category header for the checklist grid — icon, title, and an "n/m
  // complete" pill so progress is visible at a glance per group.
  const renderChecklistCategoryHeader = (icon, title, completedCount, totalCount) => (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <h6 className="text-primary mb-0" style={{ fontSize: '13px' }}>
        <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '18px' }}>{icon}</i>
        {title}
      </h6>
      <Badge
        bg={completedCount === totalCount ? 'success' : 'light'}
        className="fw-normal"
        style={completedCount !== totalCount ? { color: '#8a8f98', border: '1px solid #dcdfe3' } : {}}
      >
        {completedCount}/{totalCount}
      </Badge>
    </div>
  );

  const checklistItemGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '8px'
  };

  return (
    // onBlur here (not on individual inputs) relies on React's synthetic
    // blur bubbling all the way up the tree - including through portal-
    // rendered content like the signature Modal below, since React bubbles
    // by component tree position, not DOM position - so one handler catches
    // every field's blur across the whole form. See performAutosave/
    // handleFormBlur above for what actually runs.
    <div onBlur={handleFormBlur}>
      <style>{`
        .form-control, .form-select {
          border-radius: 8px;
          border: 2px solid #e0e0e0;
          padding: 10px 15px;
          transition: all 0.3s ease;
        }
        .form-control:focus, .form-select:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.15);
        }
        .form-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
        }
      `}</style>
      {/* No .bg-primary override here anymore — this <style> tag is global
          (not CSS-modules/styled-jsx scoped), so a rule here used to hijack
          Bootstrap's real .bg-primary utility everywhere else in the app for
          as long as this form stayed mounted, replacing the theme's actual
          primary color with an off-brand blue-violet gradient. Bootstrap's
          own .bg-primary already renders the theme color correctly. */}

      {/* Main Form Section */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="text-center mb-4 pb-4 border-bottom">
            <h3 className="text-dark mb-2 fw-bold">Registration Form</h3>
            <p className="text-muted mb-0">Please fill in all required information to complete the registration</p>
          </div>

          {remarks && (
            <div className="alert alert-info border-0" style={{ backgroundColor: '#e7f3ff', borderLeft: '4px solid #2196F3' }}>
              <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
              {remarks}
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Child Information Section */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">child_care</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Child Information</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_first_name"
                      type="text"
                      value={formData.child_first_name}
                      onChange={(e) => handleInputChange('child_first_name', e.target.value)}
                      placeholder="Child's first name"
                    />
                    <Form.Text className="text-muted">
                      Auto-filled from profile — you can change it if needed
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_last_name"
                      type="text"
                      value={formData.child_last_name}
                      onChange={(e) => handleInputChange('child_last_name', e.target.value)}
                      placeholder="Child's last name"
                    />
                    <Form.Text className="text-muted">
                      Auto-filled from profile — you can change it if needed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_dob"
                      type="date"
                      value={formData.child_dob}
                      onChange={(e) => handleInputChange('child_dob', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.child_age}
                      readOnly
                      placeholder="Calculated from DOB"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                    <AddressAutocomplete
                      key={`child-address-${formData.id || 'new'}`}
                      inputId="child_address"
                      onSelect={(data) => {
                        // Ensure address data is properly set
                        if (data && data.address) {
                          handleAddressSelect('child', data);
                        }
                      }}
                      defaultAddress={formData.child_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_city"
                      type="text"
                      value={formData.child_city}
                      onChange={(e) => handleInputChange('child_city', e.target.value)}
                      placeholder="Enter city"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="child_postal_code"
                      type="text"
                      value={formData.child_postal_code}
                      onChange={(e) => handleInputChange('child_postal_code', e.target.value)}
                      placeholder="Enter postal/ZIP code"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Parent/Guardian Information */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">family_restroom</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Parent/Guardian Information</h5>
              </div>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mother's Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_name"
                      type="text"
                      value={formData.parent1_last_name !== null && formData.parent1_last_name !== undefined ? 
                        `${formData.parent1_first_name || ''} ${formData.parent1_last_name}` : 
                        (formData.parent1_first_name || '')}
                      onChange={(e) => {
                        const fullName = e.target.value;
                        
                        // Check if there's a space in the input
                        if (fullName.includes(' ')) {
                          // Split on first space only
                          const firstSpaceIndex = fullName.indexOf(' ');
                          const firstName = fullName.substring(0, firstSpaceIndex);
                          const lastName = fullName.substring(firstSpaceIndex + 1);
                          handleInputChange('parent1_first_name', firstName);
                          handleInputChange('parent1_last_name', lastName);
                        } else {
                          // No space yet - everything goes to first name
                          handleInputChange('parent1_first_name', fullName);
                          handleInputChange('parent1_last_name', null);
                        }
                      }}
                      onBlur={(e) => {
                        // On blur, clean up extra spaces
                        const trimmedName = e.target.value.trim();
                        const parts = trimmedName.split(/\s+/).filter(p => p);
                        
                        if (parts.length === 0) {
                          handleInputChange('parent1_first_name', '');
                          handleInputChange('parent1_last_name', '');
                        } else if (parts.length === 1) {
                          // Single name - duplicate for submission
                          handleInputChange('parent1_first_name', parts[0]);
                          handleInputChange('parent1_last_name', parts[0]);
                        } else {
                          handleInputChange('parent1_first_name', parts[0]);
                          handleInputChange('parent1_last_name', parts.slice(1).join(' '));
                        }
                      }}
                      placeholder="Enter mother's full name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Phone <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_phone"
                      type="text"
                      value={formData.parent1_phone}
                      onChange={(e) => handleInputChange('parent1_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_cell}
                      onChange={(e) => handleInputChange('parent1_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent1-address-${formData.id || 'new'}`}
                      inputId="parent1_address"
                      onSelect={(data) => handleAddressSelect('parent1', data)}
                      defaultAddress={formData.parent1_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_city}
                      onChange={(e) => handleInputChange('parent1_city', e.target.value)}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_zip}
                      onChange={(e) => handleInputChange('parent1_zip', e.target.value)}
                      placeholder="Postal/ZIP Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_phone}
                      onChange={(e) => handleInputChange('parent1_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent1-work-address-${formData.id || 'new'}`}
                      inputId="parent1_work_address"
                      onSelect={(data) => handleAddressSelect('parent1_work', data)}
                      defaultAddress={formData.parent1_work_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_city}
                      onChange={(e) => handleInputChange('parent1_work_city', e.target.value)}
                      placeholder="Work city"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent1_work_postal_code}
                      onChange={(e) => handleInputChange('parent1_work_postal_code', e.target.value)}
                      placeholder="Work postal/ZIP code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (for communication and SeeSaw) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="parent1_email"
                      type="email"
                      value={formData.parent1_email}
                      onChange={(e) => handleInputChange('parent1_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Father's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_last_name !== null && formData.parent2_last_name !== undefined ? 
                        `${formData.parent2_first_name || ''} ${formData.parent2_last_name}` : 
                        (formData.parent2_first_name || '')}
                      onChange={(e) => {
                        const fullName = e.target.value;
                        
                        // Check if there's a space in the input
                        if (fullName.includes(' ')) {
                          // Split on first space only
                          const firstSpaceIndex = fullName.indexOf(' ');
                          const firstName = fullName.substring(0, firstSpaceIndex);
                          const lastName = fullName.substring(firstSpaceIndex + 1);
                          handleInputChange('parent2_first_name', firstName);
                          handleInputChange('parent2_last_name', lastName);
                        } else {
                          // No space yet - everything goes to first name
                          handleInputChange('parent2_first_name', fullName);
                          handleInputChange('parent2_last_name', null);
                        }
                      }}
                      onBlur={(e) => {
                        // On blur, clean up extra spaces
                        const trimmedName = e.target.value.trim();
                        const parts = trimmedName.split(/\s+/).filter(p => p);
                        
                        if (parts.length === 0) {
                          handleInputChange('parent2_first_name', '');
                          handleInputChange('parent2_last_name', '');
                        } else if (parts.length === 1) {
                          // Single name - duplicate for submission
                          handleInputChange('parent2_first_name', parts[0]);
                          handleInputChange('parent2_last_name', parts[0]);
                        } else {
                          handleInputChange('parent2_first_name', parts[0]);
                          handleInputChange('parent2_last_name', parts.slice(1).join(' '));
                        }
                      }}
                      placeholder="Enter father's full name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <div style={{
                      backgroundColor: '#f0f8ff',
                      border: '1px solid #d4e4f7',
                      borderRadius: '8px',
                      padding: '12px 15px',
                      display: 'inline-block'
                    }}>
                      <Form.Check
                        type="checkbox"
                        id="same-as-mother-address"
                        label={<span style={{ fontWeight: '500', fontSize: '13px', color: '#5a6c7d' }}>Same as Mother's Address</span>}
                        checked={formData.same_as_mother_address}
                        onChange={(e) => handleInputChange('same_as_mother_address', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_phone}
                      onChange={(e) => handleInputChange('parent2_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_cell}
                      onChange={(e) => handleInputChange('parent2_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent2-address-${formData.id || 'new'}`}
                      inputId="parent2_address"
                      onSelect={(data) => handleAddressSelect('parent2', data)}
                      defaultAddress={formData.parent2_address}
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_city}
                      onChange={(e) => handleInputChange('parent2_city', e.target.value)}
                      placeholder="City"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_zip}
                      onChange={(e) => handleInputChange('parent2_zip', e.target.value)}
                      placeholder="Postal/ZIP Code"
                      disabled={formData.same_as_mother_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_phone}
                      onChange={(e) => handleInputChange('parent2_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`parent2-work-address-${formData.id || 'new'}`}
                      inputId="parent2_work_address"
                      onSelect={(data) => handleAddressSelect('parent2_work', data)}
                      defaultAddress={formData.parent2_work_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_city}
                      onChange={(e) => handleInputChange('parent2_work_city', e.target.value)}
                      placeholder="Work city"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.parent2_work_postal_code}
                      onChange={(e) => handleInputChange('parent2_work_postal_code', e.target.value)}
                      placeholder="Work postal/ZIP code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (for communication and SeeSaw)</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.parent2_email}
                      onChange={(e) => handleInputChange('parent2_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Emergency Contact Information */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">contact_emergency</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Emergency Contact (Not a Parent)</h5>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name & Relationship</Form.Label>
                    <Form.Control
                      type="text"
                      value={`${formData.emergency_contact_name}${formData.emergency_contact_relation ? ' - ' + formData.emergency_contact_relation : ''}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        const parts = value.split(' - ');
                        const name = parts[0] || '';
                        const relation = parts[1] || '';
                        handleInputChange('emergency_contact_name', name);
                        handleInputChange('emergency_contact_relation', relation);
                      }}
                      placeholder="Full name - Relationship (e.g., John Doe - Uncle)"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Home Address</Form.Label>
                    <AddressAutocomplete
                      key={`emergency-contact-address-${formData.id || 'new'}`}
                      inputId="emergency_contact_address"
                      onSelect={(data) => handleAddressSelect('emergency_contact', data)}
                      defaultAddress={formData.emergency_contact_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_city}
                      onChange={(e) => handleInputChange('emergency_contact_city', e.target.value)}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_postal_code}
                      onChange={(e) => handleInputChange('emergency_contact_postal_code', e.target.value)}
                      placeholder="Postal/ZIP Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cell</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_cell}
                      onChange={(e) => handleInputChange('emergency_contact_cell', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_contact_work_phone}
                      onChange={(e) => handleInputChange('emergency_contact_work_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Address</Form.Label>
                    <AddressAutocomplete
                      key={`emergency-work-address-${formData.id || 'new'}`}
                      inputId="emergency_contact_work_address"
                      onSelect={(data) => handleAddressSelect('emergency_contact_work', data)}
                      defaultAddress={formData.emergency_contact_work_address}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.emergency_contact_email}
                      onChange={(e) => handleInputChange('emergency_contact_email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Authorized Pick-Up List */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                  <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                    <i className="material-symbols-outlined">group_add</i>
                  </div>
                  <h5 className="mb-0 fw-bold text-dark">Authorized Pick-Up List</h5>
                </div>
                {activePickupPersons.length < 4 && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      const nextNum = Math.max(...activePickupPersons) + 1;
                      if (nextNum <= 4) {
                        setActivePickupPersons([...activePickupPersons, nextNum]);
                      }
                    }}
                  >
                    <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>add_circle</i>
                    Add Person
                  </Button>
                )}
              </div>
              <p className="text-muted mb-4">List individuals (besides parents and emergency contact) authorized to pick up your child. Include full name, relationship, and phone number.</p>

              {activePickupPersons.map((num) => (
                <div key={num} className="mb-4 p-3 position-relative" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #dee2e6' }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="text-secondary mb-0">Authorized Person #{num}</h6>
                    {activePickupPersons.length > 1 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => {
                          // Clear the data for this person
                          handleInputChange(`pickup${num}_name`, '');
                          handleInputChange(`pickup${num}_phone`, '');
                          handleInputChange(`pickup${num}_cell`, '');
                          handleInputChange(`pickup${num}_address`, '');
                          handleInputChange(`pickup${num}_city`, '');
                          handleInputChange(`pickup${num}_postal_code`, '');
                          // Remove this person from the active list
                          setActivePickupPersons(activePickupPersons.filter(n => n !== num));
                        }}
                      >
                        <i className="material-symbols-outlined" style={{ fontSize: '20px', verticalAlign: 'middle' }}>delete</i>
                        Remove
                      </Button>
                    )}
                  </div>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_name`]}
                          onChange={(e) => handleInputChange(`pickup${num}_name`, e.target.value)}
                          placeholder="Full name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_phone`]}
                          onChange={(e) => handleInputChange(`pickup${num}_phone`, e.target.value)}
                          placeholder="+1-XXX-XXX-XXXX"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Cell</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_cell`]}
                          onChange={(e) => handleInputChange(`pickup${num}_cell`, e.target.value)}
                          placeholder="+1-XXX-XXX-XXXX"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <AddressAutocomplete
                          key={`pickup${num}-address-${formData.id || 'new'}`}
                          inputId={`pickup${num}_address`}
                          onSelect={(data) => handleAddressSelect(`pickup${num}`, data)}
                          defaultAddress={formData[`pickup${num}_address`]}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_city`]}
                          onChange={(e) => handleInputChange(`pickup${num}_city`, e.target.value)}
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postal/ZIP Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData[`pickup${num}_postal_code`]}
                          onChange={(e) => handleInputChange(`pickup${num}_postal_code`, e.target.value)}
                          placeholder="Postal/ZIP Code"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            {/* Arrival and Departure */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">schedule</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Arrival and Departure</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Drop-off Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.dropoff_time}
                      onChange={(e) => handleInputChange('dropoff_time', e.target.value)}
                    />
                    <Form.Text className="text-muted">Fixed hours required for full-day attendance</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pick-up Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.pickup_time}
                      onChange={(e) => handleInputChange('pickup_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent/Guardian Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={arrivalSignaturePadRef}
                        clearOnResize={false}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        arrivalSignaturePadRef.current?.clear();
                        handleInputChange('parent_guardian_signature', '');
                      }}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Withdrawal Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.withdrawal_date}
                      onChange={(e) => handleInputChange('withdrawal_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Emergency Medical Authorization Form */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">local_hospital</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Emergency Medical Authorization Form</h5>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent/Guardian Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_auth_parent_name}
                      onChange={(e) => handleInputChange('emergency_auth_parent_name', e.target.value)}
                      placeholder="Parent/Guardian name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.emergency_auth_child_name}
                      onChange={(e) => handleInputChange('emergency_auth_child_name', e.target.value)}
                      placeholder="Child's name"
                    />
                    <Form.Text className="text-muted">
                      Auto-filled from profile — you can change it if needed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="mb-4 p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <p className="mb-0">
                  I, <strong>{formData.emergency_auth_parent_name || '____________________'}</strong>, parent/guardian of <strong>{formData.emergency_auth_child_name || '____________________'}</strong>, authorize Harmony Arts Daycare and Education Centre to obtain emergency medical treatment if necessary.
                </p>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Family Physician's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.family_physician_name}
                      onChange={(e) => handleInputChange('family_physician_name', e.target.value)}
                      placeholder="Doctor's name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Physician Address</Form.Label>
                    <AddressAutocomplete
                      key={`physician-address-${formData.id || 'new'}`}
                      inputId="physician_address"
                      onSelect={(data) => handleAddressSelect('physician', data)}
                      defaultAddress={formData.physician_address}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Physician Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.physician_phone}
                      onChange={(e) => handleInputChange('physician_phone', e.target.value)}
                      placeholder="+1-XXX-XXX-XXXX"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Medications</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.current_medications}
                      onChange={(e) => handleInputChange('current_medications', e.target.value)}
                      placeholder="List current medications"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Allergies/Relevant Health Info</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.health_allergies}
                      onChange={(e) => handleInputChange('health_allergies', e.target.value)}
                      placeholder="List allergies and relevant health information"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sleep Time (Night)</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.sleep_time}
                      onChange={(e) => handleInputChange('sleep_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Wake Time (Morning)</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.wake_time}
                      onChange={(e) => handleInputChange('wake_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Favourite Activity</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.favourite_activity}
                      onChange={(e) => handleInputChange('favourite_activity', e.target.value)}
                      placeholder="Favourite activity"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={emergencyMedicalSignaturePadRef}
                        clearOnResize={false}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        emergencyMedicalSignaturePadRef.current?.clear();
                        handleInputChange('emergency_auth_signature', '');
                      }}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.emergency_auth_date}
                      onChange={(e) => handleInputChange('emergency_auth_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Child Information Page */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">child_care</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Child Information Page</h5>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Involvement with Child-Related Agencies (e.g., speech therapy)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.agency_involvement}
                      onChange={(e) => handleInputChange('agency_involvement', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vision History / Glasses Usage</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.vision_history}
                      onChange={(e) => handleInputChange('vision_history', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hearing Concerns / Audiologist Visits</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.hearing_concerns}
                      onChange={(e) => handleInputChange('hearing_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Any Diagnoses (e.g., ADHD, delays)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.diagnoses}
                      onChange={(e) => handleInputChange('diagnoses', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Communicable Disease History</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.communicable_disease}
                      onChange={(e) => handleInputChange('communicable_disease', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Eating/Appetite Concerns</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.eating_concerns}
                      onChange={(e) => handleInputChange('eating_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Developmental Concerns</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.developmental_concerns}
                      onChange={(e) => handleInputChange('developmental_concerns', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pets at Home</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.pets_at_home}
                      onChange={(e) => handleInputChange('pets_at_home', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Siblings / Family Info</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.siblings_info}
                      onChange={(e) => handleInputChange('siblings_info', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Child's Favourite Toy or Activity</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.favourite_toy}
                      onChange={(e) => handleInputChange('favourite_toy', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Media Release Waiver */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">photo_camera</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Media Release Waiver</h5>
              </div>
              <p className="text-muted mb-4">Harmony Arts Daycare and Education Centre may use photos/videos on Instagram, SeeSaw, and through media partners (e.g., Brant News, The Expositor, Rogers).</p>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="radio"
                      name="media_release"
                      label="I AGREE to media release"
                      checked={formData.media_release_agree === true || formData.media_release_agree === 1 || formData.media_release_agree === '1'}
                      onChange={() => handleInputChange('media_release_agree', true)}
                    />
                    <Form.Check
                      style={{ marginLeft: "20px" }}
                      type="radio"
                      name="media_release"
                      label="I DISAGREE with media release"
                      checked={formData.media_release_agree === false || formData.media_release_agree === 0 || formData.media_release_agree === '0'}
                      onChange={() => handleInputChange('media_release_agree', false)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {(formData.media_release_agree === true || formData.media_release_agree === 1 || formData.media_release_agree === '1') && (
                <div className="mb-4 p-3" style={{ backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #28a745' }}>
                  <h6 className="text-success mb-3">If you AGREE:</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_child_name}
                          onChange={(e) => handleInputChange('media_release_child_name', e.target.value)}
                          placeholder="Child's name"
                        />
                        <Form.Text className="text-muted">
                          Auto-filled from profile — you can change it if needed
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="media_release_date"
                          type="date"
                          value={formData.media_release_date}
                          onChange={(e) => handleInputChange('media_release_date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="media_release_parent_name"
                          type="text"
                          value={formData.media_release_parent_name}
                          onChange={(e) => handleInputChange('media_release_parent_name', e.target.value)}
                          placeholder="Parent's name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                        <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                          <SignatureCanvas
                            ref={mediaReleaseSignaturePadRef}
                            clearOnResize={false}
                            canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '100%' }
                            }}
                          />
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            mediaReleaseSignaturePadRef.current?.clear();
                            handleInputChange('media_release_signature', '');
                          }}
                          type="button"
                        >
                          Clear Signature
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {(formData.media_release_agree === false || formData.media_release_agree === 0 || formData.media_release_agree === '0') && (
                <div className="mb-4 p-3" style={{ backgroundColor: '#f8d7da', borderRadius: '8px', border: '1px solid #dc3545' }}>
                  <h6 className="text-danger mb-3">If you DISAGREE:</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_child_name}
                          onChange={(e) => handleInputChange('media_release_child_name', e.target.value)}
                          placeholder="Child's name"
                        />
                        <Form.Text className="text-muted">
                          Auto-filled from profile — you can change it if needed
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.media_release_date}
                          onChange={(e) => handleInputChange('media_release_date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent's Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.media_release_parent_name}
                          onChange={(e) => handleInputChange('media_release_parent_name', e.target.value)}
                          placeholder="Parent's name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                        <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                          <SignatureCanvas
                            ref={mediaReleaseSignaturePadRef}
                            clearOnResize={false}
                            canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '100%' }
                            }}
                          />
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            mediaReleaseSignaturePadRef.current?.clear();
                            handleInputChange('media_release_signature', '');
                          }}
                          type="button"
                        >
                          Clear Signature
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Off-Site Permission Form */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">directions_walk</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Off-Site Permission Form</h5>
              </div>
              
              <div className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="offsite-permission-agree"
                  checked={formData.offsite_agree}
                  onChange={(e) => handleInputChange('offsite_agree', e.target.checked)}
                  label={
                    <span>
                      <strong>I give permission</strong> for Harmony Arts Daycare and Education Centre staff to take my child on supervised off-site walks around the neighbourhood.
                    </span>
                  }
                />
              </div>
              
              {formData.offsite_agree && (
                <div>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent/Guardian Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          id="offsite_parent_name"
                          type="text"
                          value={formData.offsite_parent_name}
                          onChange={(e) => handleInputChange('offsite_parent_name', e.target.value)}
                          placeholder="Parent/Guardian name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Child's Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.offsite_child_name}
                      onChange={(e) => handleInputChange('offsite_child_name', e.target.value)}
                      placeholder="Child's name"
                    />
                    <Form.Text className="text-muted">
                      Auto-filled from profile — you can change it if needed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="mb-4 p-3" style={{ backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #28a745' }}>
                <p className="mb-0">
                  I, <strong>{formData.offsite_parent_name || '____________________'}</strong>, give permission for Harmony Arts Daycare and Education Centre staff to take my child, <strong>{formData.offsite_child_name || '____________________'}</strong>, on supervised off-site walks around the neighbourhood.
                </p>
              </div>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={offsiteSignaturePadRef}
                        clearOnResize={false}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        offsiteSignaturePadRef.current?.clear();
                        handleInputChange('offsite_signature', '');
                      }}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      id="offsite_date"
                      type="date"
                      value={formData.offsite_date}
                      onChange={(e) => handleInputChange('offsite_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
                </div>
              )}
            </div>

            {/* Contract Terms and Conditions */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">gavel</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Contract Terms and Conditions <span className="text-danger">*</span></h5>
              </div>
              {generalSettingsData?.handbook && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  href={generalSettingsData.handbook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-inline-flex align-items-center mb-3"
                >
                  <i className="material-symbols-outlined me-2" style={{ fontSize: '18px' }}>menu_book</i>
                  Show Handbook
                </Button>
              )}
              <p className="text-muted mb-4">Please read each clause carefully. Click the checkbox to sign each clause individually:</p>
              {[
                "I have reviewed and understood the Parent Policies Handbook (PPH).",
                "I agree to comply with all policies, including health, safety, and ministry regulations.",
                "I acknowledge that custody/visitation disputes require valid court documentation.",
                "I understand that while parental concerns are welcomed, certain policies are not subject to parental discretion.",
                "All required enrollment forms and documents will be submitted before the child's start date.",
                "I agree that tuition is due on the 1st of every month, and non-payment may result in suspended attendance.",
                "I grant permission for emergency medical care for my child if necessary.",
                "I agree to pay all extra-curricular activity fees in advance.",
                "I understand that 30 days' written notice is required for any withdrawal or schedule change.",
                "I understand that fees are non-refundable for absences, illness, or statutory holidays.",
                "I acknowledge that enrolment may be terminated for delinquent accounts, and I am responsible for any associated collection or legal fees.",
                "I am responsible for ensuring that all authorized persons follow pick-up procedures.",
                "I understand non-compliance with these terms may result in contract termination."
              ].map((clause, index) => {
                const clauseNumber = index + 1;
                const hasSignature = formData[`contract_signature_${clauseNumber}`];
                const isChecked = formData[`contract_initial_${clauseNumber}`];

                return (
                  <div key={index} className="mb-3 p-3 border rounded" style={{ backgroundColor: isChecked ? '#e8f5e9' : '#f8f9fa', borderColor: isChecked ? '#4caf50' : '#dee2e6' }}>
                    <Row className="align-items-center">
                      <Col md={hasSignature ? 9 : 12}>
                        <Form.Check
                          type="checkbox"
                          id={`contract-${clauseNumber}`}
                          label={`${clauseNumber}. ${clause} *`}
                          checked={isChecked}
                          onChange={(e) => handleContractClauseChange(clauseNumber, e.target.checked)}
                          disabled={hasSignature}
                        />
                        {hasSignature && (
                          <div className="mt-2">
                            <Badge bg="success" className="me-2">
                              <i className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>check_circle</i>
                              {' '}Signed
                            </Badge>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => {
                                handleInputChange(`contract_initial_${clauseNumber}`, false);
                                handleInputChange(`contract_signature_${clauseNumber}`, '');
                              }}
                            >
                              <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>delete</i>
                              Remove Signature
                            </Button>
                          </div>
                        )}
                      </Col>
                      {hasSignature && (
                        <Col md={3} className="text-end">
                          <div
                            className="border rounded p-2 bg-white"
                            style={{
                              width: '120px',
                              height: '60px',
                              display: 'inline-block',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            <img
                              src={hasSignature}
                              alt={`Signature ${clauseNumber}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                );
              })}

              {/* Summary */}
              <div className="mt-4 p-3 border rounded" style={{ backgroundColor: '#e3f2fd' }}>
                <Row className="align-items-center">
                  <Col md={8}>
                    <strong>
                      <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
                      Signed Clauses: {Object.keys(formData).filter(key => key.startsWith('contract_initial_') && formData[key]).length} / 13
                    </strong>
                  </Col>
                  <Col md={4} className="text-end">
                    {Object.keys(formData).filter(key => key.startsWith('contract_initial_') && formData[key]).length === 13 ? (
                      <Badge bg="success" className="p-2">
                        <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>verified</i>
                        {' '}All Clauses Signed
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="p-2">
                        <i className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>pending</i>
                        {' '}Incomplete
                      </Badge>
                    )}
                  </Col>
                </Row>
              </div>

              {/* Parent/Guardian Information */}
              <div className="mt-4 pt-4 border-top">
                <h6 className="mb-3 fw-bold text-dark">Parent/Guardian Information</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Print Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.parent_print_name}
                        onChange={(e) => handleInputChange('parent_print_name', e.target.value)}
                        placeholder="Print your full name"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                      <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                        <SignatureCanvas
                          ref={contractParentSignaturePadRef}
                          clearOnResize={false}
                          canvasProps={{
                            className: 'signature-canvas',
                            style: { width: '100%', height: '100%' }
                          }}
                        />
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          contractParentSignaturePadRef.current?.clear();
                          handleInputChange('parent_signature', '');
                        }}
                        type="button"
                      >
                        Clear Signature
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.parent_signature_date}
                        onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Allergy Action Plan */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">health_and_safety</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Allergy Action Plan</h5>
              </div>
              <p className="text-muted mb-4">If your child has food or insect sting/bite allergies that require a documented action plan, complete this section so our staff know how to respond.</p>

              {!existingAllergyPlan && (
                <Form.Check
                  type="checkbox"
                  id="allergy-plan-opt-in"
                  checked={formData.allergy_plan_opt_in}
                  onChange={(e) => handleInputChange('allergy_plan_opt_in', e.target.checked)}
                  label={<span><strong>My child has allergies</strong> and requires a documented Allergy Action Plan.</span>}
                  className="mb-4"
                />
              )}

              {allergyPlanFieldsVisible && (
                    <div>
                      <div className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="allergy_plan_is_allergy_form"
                          label="Allergies"
                          checked={!!formData.allergy_plan_is_allergy_form}
                          onChange={() => handleInputChange('allergy_plan_is_allergy_form', formData.allergy_plan_is_allergy_form ? 0 : 1)}
                          className="mb-2 custom-checkbox"
                        />
                        <Form.Check
                          type="checkbox"
                          id="allergy_plan_is_preference_form"
                          label="Preferences"
                          style={{ marginLeft: '20px' }}
                          checked={!!formData.allergy_plan_is_preference_form}
                          onChange={() => handleInputChange('allergy_plan_is_preference_form', formData.allergy_plan_is_preference_form ? 0 : 1)}
                          className="mb-2 custom-checkbox"
                        />
                      </div>

                      <h6 className="mb-3 fw-bold text-dark">Food Allergies</h6>
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Check type="checkbox" id="allergy_plan_is_peanuts" label="Peanuts" checked={formData.allergy_plan_is_peanuts === 1} onChange={() => handleInputChange('allergy_plan_is_peanuts', formData.allergy_plan_is_peanuts === 1 ? 0 : 1)} className="mb-3 custom-checkbox" />
                          <Form.Check type="checkbox" id="allergy_plan_is_shellfish" label="Shellfish" checked={formData.allergy_plan_is_shellfish === 1} onChange={() => handleInputChange('allergy_plan_is_shellfish', formData.allergy_plan_is_shellfish === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          {formData.allergy_plan_is_shellfish === 1 && (
                            <Form.Group className="mb-3 ms-4">
                              <Form.Control type="text" placeholder="Specify shellfish type" value={formData.allergy_plan_shellfish_specify} onChange={(e) => handleInputChange('allergy_plan_shellfish_specify', e.target.value)} />
                            </Form.Group>
                          )}
                          <Form.Check type="checkbox" id="allergy_plan_is_tree_nuts" label="Tree Nuts" checked={formData.allergy_plan_is_tree_nuts === 1} onChange={() => handleInputChange('allergy_plan_is_tree_nuts', formData.allergy_plan_is_tree_nuts === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          <Form.Check type="checkbox" id="allergy_plan_is_fish" label="Fish" checked={formData.allergy_plan_is_fish === 1} onChange={() => handleInputChange('allergy_plan_is_fish', formData.allergy_plan_is_fish === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          {formData.allergy_plan_is_fish === 1 && (
                            <Form.Group className="mb-3 ms-4">
                              <Form.Control type="text" placeholder="Specify fish type" value={formData.allergy_plan_fish_specify} onChange={(e) => handleInputChange('allergy_plan_fish_specify', e.target.value)} />
                            </Form.Group>
                          )}
                        </Col>
                        <Col md={6}>
                          <Form.Check type="checkbox" id="allergy_plan_is_milk" label="Milk" checked={formData.allergy_plan_is_milk === 1} onChange={() => handleInputChange('allergy_plan_is_milk', formData.allergy_plan_is_milk === 1 ? 0 : 1)} className="mb-3 custom-checkbox" />
                          <Form.Check type="checkbox" id="allergy_plan_is_food_additives" label="Food Additives" checked={formData.allergy_plan_is_food_additives === 1} onChange={() => handleInputChange('allergy_plan_is_food_additives', formData.allergy_plan_is_food_additives === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          {formData.allergy_plan_is_food_additives === 1 && (
                            <Form.Group className="mb-3 ms-4">
                              <Form.Control type="text" placeholder="Specify food additives" value={formData.allergy_plan_food_additives_specify} onChange={(e) => handleInputChange('allergy_plan_food_additives_specify', e.target.value)} />
                            </Form.Group>
                          )}
                          <Form.Check type="checkbox" id="allergy_plan_all_dairy_products" label="All Dairy Products" checked={formData.allergy_plan_all_dairy_products === 1} onChange={() => handleInputChange('allergy_plan_all_dairy_products', formData.allergy_plan_all_dairy_products === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          <Form.Check type="checkbox" id="allergy_plan_is_other" label="Other" checked={formData.allergy_plan_is_other === 1} onChange={() => handleInputChange('allergy_plan_is_other', formData.allergy_plan_is_other === 1 ? 0 : 1)} className="mb-3 custom-checkbox" style={{ marginLeft: '20px' }} />
                          {formData.allergy_plan_is_other === 1 && (
                            <Form.Group className="mb-3 ms-4">
                              <Form.Control type="text" placeholder="Specify other allergies" value={formData.allergy_plan_other_specify} onChange={(e) => handleInputChange('allergy_plan_other_specify', e.target.value)} />
                            </Form.Group>
                          )}
                        </Col>
                      </Row>

                      <h6 className="mb-3 fw-bold text-dark">Insect Stings/Bites</h6>
                      <Form.Check type="checkbox" id="allergy_plan_insect_stings_bites" label="Has Insect Sting/Bite Allergy" checked={formData.allergy_plan_insect_stings_bites === 1} onChange={() => handleInputChange('allergy_plan_insect_stings_bites', formData.allergy_plan_insect_stings_bites === 1 ? 0 : 1)} className="mb-3 custom-checkbox" />
                      {formData.allergy_plan_insect_stings_bites === 1 && (
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Specify Insect Type</Form.Label>
                              <Form.Control type="text" placeholder="e.g., Bee sting, Wasp" value={formData.allergy_plan_insect_stings_bites_specify} onChange={(e) => handleInputChange('allergy_plan_insect_stings_bites_specify', e.target.value)} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Medication for Insect Sting/Bite</Form.Label>
                              <Form.Control as="textarea" rows={2} placeholder="e.g., Apply ice, Benadryl syrup" value={formData.allergy_plan_insect_stings_bites_medication} onChange={(e) => handleInputChange('allergy_plan_insect_stings_bites_medication', e.target.value)} />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}

                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Symptoms <span className="text-danger">*</span></Form.Label>
                            <Form.Control id="allergy_plan_symptoms" as="textarea" rows={3} placeholder="Describe allergic reaction symptoms" value={formData.allergy_plan_symptoms} onChange={(e) => handleInputChange('allergy_plan_symptoms', e.target.value)} />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Emergency Treatment Procedures <span className="text-danger">*</span></Form.Label>
                            <Form.Control id="allergy_plan_emergency_treatment_procedures" as="textarea" rows={3} placeholder="Describe emergency treatment steps" value={formData.allergy_plan_emergency_treatment_procedures} onChange={(e) => handleInputChange('allergy_plan_emergency_treatment_procedures', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Check type="checkbox" id="allergy_plan_require_epi_pen" label="Requires EpiPen" checked={formData.allergy_plan_require_epi_pen === 1} onChange={() => handleInputChange('allergy_plan_require_epi_pen', formData.allergy_plan_require_epi_pen === 1 ? 0 : 1)} className="mb-3 custom-checkbox" />
                      {formData.allergy_plan_require_epi_pen === 1 && (
                        <Form.Group className="mb-3">
                          <Form.Label>Location of EpiPen</Form.Label>
                          <Form.Control type="text" placeholder="e.g., In the classroom first-aid kit" value={formData.allergy_plan_location_of_epi_pen} onChange={(e) => handleInputChange('allergy_plan_location_of_epi_pen', e.target.value)} />
                        </Form.Group>
                      )}

                      <div className="mb-4 p-3 bg-light border rounded">
                        <p className="mb-2">By signing below, I acknowledge and agree that:</p>
                        <ul className="mb-0">
                          <li className="mb-2">I give Harmony Arts Daycare and Education Centre staff permission to administer my child's epi-pen if required.</li>
                          <li className="mb-2">I give permission for staff to call 911 immediately if my child experiences an anaphylactic reaction.</li>
                          <li className="mb-0">I am responsible for informing the daycare of any required updates to this plan.</li>
                        </ul>
                      </div>

                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Parent Signature <span className="text-danger">*</span></Form.Label>
                            <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                              <SignatureCanvas
                                ref={allergyPlanSignaturePadRef}
                                clearOnResize={false}
                                onEnd={() => { allergyPlanSignatureDirtyRef.current = true; }}
                                canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '100%' } }}
                              />
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="mt-2"
                              type="button"
                              onClick={() => {
                                allergyPlanSignaturePadRef.current?.clear();
                                allergyPlanSignatureDirtyRef.current = false;
                                handleInputChange('allergy_plan_signature', '');
                              }}
                            >
                              Clear Signature
                            </Button>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              id="allergy_plan_signature_date"
                              type="date"
                              value={formData.allergy_plan_signature_date}
                              onChange={(e) => handleInputChange('allergy_plan_signature_date', e.target.value)}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
              )}
            </div>

            {/* Lunch Plan Waiver */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">restaurant</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Lunch Plan Waiver</h5>
              </div>
              <p className="text-muted mb-4">Only complete this if you are opting your child OUT of the daycare's lunch plan.</p>

              {!existingLunchWaiver && (
                <Form.Check
                  type="checkbox"
                  id="lunch-waiver-opt-in"
                  checked={formData.lunch_waiver_opt_in}
                  onChange={(e) => handleInputChange('lunch_waiver_opt_in', e.target.checked)}
                  label={<span><strong>We are waiving the daycare's lunch plan</strong> — our child will bring lunch from home.</span>}
                  className="mb-4"
                />
              )}

              {lunchWaiverFieldsVisible && (
                    <div>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Reason for Lunch Plan Waiver <span className="text-danger">*</span></Form.Label>
                            <Form.Control id="lunch_waiver_reason" as="textarea" rows={3} placeholder="e.g., Child prefers home lunch due to allergy concerns." value={formData.lunch_waiver_reason} onChange={(e) => handleInputChange('lunch_waiver_reason', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                            <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                              <SignatureCanvas
                                ref={lunchWaiverSignaturePadRef}
                                clearOnResize={false}
                                onEnd={() => { lunchWaiverSignatureDirtyRef.current = true; }}
                                canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '100%' } }}
                              />
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="mt-2"
                              type="button"
                              onClick={() => {
                                lunchWaiverSignaturePadRef.current?.clear();
                                lunchWaiverSignatureDirtyRef.current = false;
                                handleInputChange('lunch_waiver_signature', '');
                              }}
                            >
                              Clear Signature
                            </Button>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              id="lunch_waiver_signature_date"
                              type="date"
                              value={formData.lunch_waiver_signature_date}
                              onChange={(e) => handleInputChange('lunch_waiver_signature_date', e.target.value)}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
              )}
            </div>

            {/* No Peanut Policy */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">no_food</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">No Peanut Policy <span className="text-danger">*</span></h5>
              </div>

              <div>
                <div className="alert alert-default" role="alert">
                  <h6 className="alert-heading">Dear Parents,</h6>
                  <p>
                    This letter is to inform you that a student/teacher in your child's classroom has a severe peanut/nut allergy.
                    Strict avoidance of peanut/nut products is the only way to prevent a life-threatening allergic reaction.
                    We are asking your assistance in providing the student with a safe learning environment.
                  </p>
                  <p>
                    If exposed to peanuts/nuts the student/teacher may develop a life-threatening allergic reaction that requires emergency medical treatment.
                    To reduce the risk of exposure, the classroom will be peanut/nut free. Please do not send any peanut or tree nut containing products
                    for your child to eat during snack in the classroom. If your child has eaten peanuts, peanut butter, or nuts prior to coming to school,
                    please be sure your child's hands have been thoroughly washed prior to entering the school.
                  </p>
                  <hr />
                  <h6 className="text-danger mb-0">NO PEANUTS OR NUTS, PLEASE</h6>
                </div>

                <Form.Check
                  type="checkbox"
                  id="peanut_policy_acknowledge"
                  checked={formData.peanut_policy_acknowledge}
                  onChange={(e) => handleInputChange('peanut_policy_acknowledge', e.target.checked)}
                  label={<span>I have read and understand the peanut/nut free classroom procedures, and I agree to do my part in keeping the classroom peanut and nut free. <span className="text-danger">*</span></span>}
                  className="mb-4"
                />

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                      <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                        <SignatureCanvas
                          ref={peanutPolicySignaturePadRef}
                          clearOnResize={false}
                          onEnd={() => { peanutPolicySignatureDirtyRef.current = true; }}
                          canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '100%' } }}
                        />
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        type="button"
                        onClick={() => {
                          peanutPolicySignaturePadRef.current?.clear();
                          peanutPolicySignatureDirtyRef.current = false;
                          handleInputChange('peanut_policy_signature', '');
                        }}
                      >
                        Clear Signature
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        id="peanut_policy_signature_date"
                        type="date"
                        value={formData.peanut_policy_signature_date}
                        onChange={(e) => handleInputChange('peanut_policy_signature_date', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Required Document Uploads */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">upload_file</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Required Document Uploads</h5>
              </div>
              <p className="text-muted mb-3">
                Please upload clear photos or scans (JPG, PNG or PDF) of the following. Items marked <span className="text-danger fw-medium">*</span> are mandatory and must be uploaded before this registration can be submitted.
              </p>
              <Row>
                {renderDocumentUploadCard('Health Forms', 'Immunization records', 'health_immunization_document', 'upload-health-forms', true)}
                {renderDocumentUploadCard('Medical/Assessment Reports', 'If any are applicable', 'medical_reports_document', 'upload-medical-reports', true)}
                {renderDocumentUploadCard('Birth Certificate', null, 'birth_certificate_document', 'upload-birth-cert', true)}
              </Row>
            </div>

            {/* Registration Checklist Summary */}
            <div className="mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center mb-4">
                <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                  <i className="material-symbols-outlined">checklist</i>
                </div>
                <h5 className="mb-0 fw-bold text-dark">Registration Checklist Summary</h5>
              </div>
              <p className="text-muted mb-3">These are filled in automatically from the sections above (or once you upload the matching document) — nothing here needs to be checked by hand.</p>
              {(() => {
                // Computed fresh from formData every render - see
                // computeDerivedChecklist's comment for why this can't be a
                // cached state field.
                const derivedChecklist = computeDerivedChecklist(formData, checklistActiveRecords);
                // Overall progress only counts items actually shown below -
                // checklist_debit_form stays in derivedChecklist itself
                // (other things, like the submit payload, still read it),
                // but while its "Payment" category is hidden
                // (SHOW_PAYMENT_CHECKLIST_CATEGORY) it shouldn't inflate
                // this total or its "always complete" count toward one the
                // parent can't actually see.
                const checklistValues = Object.entries(derivedChecklist)
                  .filter(([key]) => SHOW_PAYMENT_CHECKLIST_CATEGORY || key !== 'checklist_debit_form')
                  .map(([, value]) => value);
                const completedCount = checklistValues.filter(Boolean).length;
                const totalCount = checklistValues.length;
                const percent = Math.round((completedCount / totalCount) * 100);
                return (
                  <>
                    <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e3e6ea' }}>
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span className="fw-medium" style={{ fontSize: '13px' }}>Checklist Progress</span>
                        <span className="small text-muted">{completedCount} of {totalCount} complete</span>
                      </div>
                      <ProgressBar
                        now={percent}
                        variant={percent === 100 ? 'success' : 'primary'}
                        style={{ height: '6px' }}
                      />
                    </div>
                    <div className="p-3 mb-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                      <div className="mb-3">
                        {renderChecklistCategoryHeader('description', 'Required Forms', [
                          derivedChecklist.checklist_registration_form, derivedChecklist.checklist_emergency_form,
                          derivedChecklist.checklist_child_info_page, derivedChecklist.checklist_all_about_me
                        ].filter(Boolean).length, 4)}
                        <div style={checklistItemGridStyle}>
                          {renderAutoChecklistItem('Registration Form', derivedChecklist.checklist_registration_form, 'check-registration-form')}
                          {renderAutoChecklistItem('Emergency Form', derivedChecklist.checklist_emergency_form, 'check-emergency-form')}
                          {renderAutoChecklistItem('Child Information Page', derivedChecklist.checklist_child_info_page, 'check-child-info')}
                          {renderAutoChecklistItem('"All About Me" Form', derivedChecklist.checklist_all_about_me, 'check-all-about-me')}
                        </div>
                      </div>

                      <div className="mb-3">
                        {renderChecklistCategoryHeader('medical_services', 'Health & Medical', [
                          derivedChecklist.checklist_health_forms, derivedChecklist.checklist_medical_reports
                        ].filter(Boolean).length, 2)}
                        <div style={checklistItemGridStyle}>
                          {renderAutoChecklistItem('Health Forms (Immunization)', derivedChecklist.checklist_health_forms, 'check-health-forms', 'Upload in Required Document Uploads above')}
                          {renderAutoChecklistItem('Relevant medical/assessment reports (if any)', derivedChecklist.checklist_medical_reports, 'check-medical-reports', 'Upload in Required Document Uploads above')}
                        </div>
                      </div>

                      <div className="mb-3">
                        {renderChecklistCategoryHeader('gavel', 'Legal & Agreements', [
                          derivedChecklist.checklist_birth_certificate, derivedChecklist.checklist_signed_contract, derivedChecklist.checklist_parent_handbook,
                          derivedChecklist.checklist_waivers, derivedChecklist.checklist_offsite_form, derivedChecklist.checklist_meal_waiver
                        ].filter(Boolean).length, 6)}
                        <div style={checklistItemGridStyle}>
                          {renderAutoChecklistItem('Birth Certificate', derivedChecklist.checklist_birth_certificate, 'check-birth-cert', 'Upload in Required Document Uploads above')}
                          {renderAutoChecklistItem('Signed Contract', derivedChecklist.checklist_signed_contract, 'check-signed-contract', 'Awaiting all clauses signed')}
                          {renderAutoChecklistItem('Read and understood Parent Handbook', derivedChecklist.checklist_parent_handbook, 'check-parent-handbook', 'Awaiting clause 1 signature')}
                          {renderAutoChecklistItem('No Peanut Policy', derivedChecklist.checklist_waivers, 'check-waivers', 'Complete the No Peanut Policy section above')}
                          {renderAutoChecklistItem('Off-Site Permission Form', derivedChecklist.checklist_offsite_form, 'check-offsite-form', 'Awaiting signature above')}
                          {renderAutoChecklistItem('Meal Waiver', derivedChecklist.checklist_meal_waiver, 'check-meal-waiver', 'Complete the Lunch Plan Waiver section above')}
                        </div>
                      </div>

                      {SHOW_PAYMENT_CHECKLIST_CATEGORY && (
                        <div>
                          {renderChecklistCategoryHeader('payments', 'Payment', derivedChecklist.checklist_debit_form ? 1 : 0, 1)}
                          <div style={checklistItemGridStyle}>
                            {renderAutoChecklistItem('Pre-Authorized Debit Form', derivedChecklist.checklist_debit_form, 'check-debit-form')}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
              <Row className="mt-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Parent Signature <span className="text-danger">*</span></Form.Label>
                    <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '150px' }}>
                      <SignatureCanvas
                        ref={checklistSignaturePadRef}
                        clearOnResize={false}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '100%' }
                        }}
                      />
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        checklistSignaturePadRef.current?.clear();
                        handleInputChange('parent_checklist_signature', '');
                      }}
                      type="button"
                    >
                      Clear Signature
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.parent_checklist_date}
                      onChange={(e) => handleInputChange('parent_checklist_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-4 p-4 text-center" style={{ backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #2196F3' }}>
                <h5 className="text-primary mb-0">Thank you for choosing Harmony Arts Daycare and Education Centre!</h5>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="d-flex align-items-center justify-content-between gap-3 pt-4 mt-4 border-top">
              <div className="small text-muted d-flex align-items-center gap-2">
                {autoSaveStatus === 'saving' && (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving draft...
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <i className="material-symbols-outlined text-success" style={{ fontSize: '16px' }}>check_circle</i>
                    Draft saved - your progress won't be lost
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <>
                    <i className="material-symbols-outlined text-warning" style={{ fontSize: '16px' }}>warning</i>
                    Couldn't save your draft{autoSaveError ? `: ${autoSaveError}` : ''} - will retry automatically
                  </>
                )}
                {autoSaveStatus === 'idle' && (
                  <span>Your progress is saved automatically as you go.</span>
                )}
              </div>
              <div className="d-flex gap-3">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={handleCancel}
                disabled={submitLoading}
                className="px-5"
              >
                <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>close</i>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={submitLoading}
                className="px-5"
              >
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>check_circle</i>
                    Submit Registration
                  </>
                )}
              </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Contract Clause Signature Modal */}
      <Modal show={showSignatureModal} onHide={handleCloseSignatureModal} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>draw</i>
            Sign Clause {currentClauseIndex}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6 className="text-muted">Clause {currentClauseIndex}:</h6>
            <p className="fw-bold">
              {currentClauseIndex && [
                "I have reviewed and understood the Parent Policies Handbook (PPH).",
                "I agree to comply with all policies, including health, safety, and ministry regulations.",
                "I acknowledge that custody/visitation disputes require valid court documentation.",
                "I understand that while parental concerns are welcomed, certain policies are not subject to parental discretion.",
                "All required enrollment forms and documents will be submitted before the child's start date.",
                "I agree that tuition is due on the 1st of every month, and non-payment may result in suspended attendance.",
                "I grant permission for emergency medical care for my child if necessary.",
                "I agree to pay all extra-curricular activity fees in advance.",
                "I understand that 30 days' written notice is required for any withdrawal or schedule change.",
                "I understand that fees are non-refundable for absences, illness, or statutory holidays.",
                "I acknowledge that enrolment may be terminated for delinquent accounts, and I am responsible for any associated collection or legal fees.",
                "I am responsible for ensuring that all authorized persons follow pick-up procedures.",
                "I understand non-compliance with these terms may result in contract termination."
              ][currentClauseIndex - 1]}
            </p>
          </div>
          <div className="alert alert-info">
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>info</i>
            Please sign in the box below to acknowledge this clause.
          </div>
          <div className="border rounded p-2 bg-light" style={{ width: '100%', height: '200px' }}>
            <SignatureCanvas
              ref={contractClauseSignatureRef}
              clearOnResize={false}
              canvasProps={{
                className: 'signature-canvas',
                style: { width: '100%', height: '100%', backgroundColor: 'white' }
              }}
            />
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mt-2"
            onClick={() => contractClauseSignatureRef.current?.clear()}
          >
            <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>refresh</i>
            Clear Signature
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSignatureModal} disabled={signatureUploading}>
            <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>close</i>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSignature}
            disabled={signatureUploading}
          >
            {signatureUploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>check_circle</i>
                Save Signature
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// A stray uncaught error anywhere in this (large, signature-heavy) form would
// otherwise unmount the entire tree and leave the parent looking at a blank
// page mid-registration, with no idea what happened. Wrap it so that instead
// they get a visible message and a retry button. Retrying remounts the form
// fresh (any saved draft is reloaded from the server via `id`/edit mode, the
// same as a normal page load), which is still far better than a silent blank
// screen with no path forward.
const ChildRegistrationFormComp = (props) => (
   <ErrorBoundary message="The registration form ran into an unexpected error. Tap Try Again to reload the form, or refresh the page if the problem repeats.">
      <ChildRegistrationFormCompInner {...props} />
   </ErrorBoundary>
);

export default ChildRegistrationFormComp;
